import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import {
  Box,
  CircularProgress,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import VoiceRecorder, {
  getAudioUploadMetadata,
  getPreferredAudioRecordingFormat,
} from "./VoiceRecorder";
import ViewerHeader from "./ViewerHeader";
import EnvironmentMap from "./EnvironmentMap";
import Label from "./Label";
import MicAccessPopup from "./MicAccessPopup";
import StartGuideDialog from "./StartGuideDialog";
import GuidePrototypes from "./Guideprototypes";
import SessionTimelinePanel, { SessionTimelineToggleButton } from "./SessionTimelinePanel";

import ExperiencesAPI from "../services/ExperiencesAPI";
import ContentAPI from "../services/ContentAPI";
import AssistantAPI from "../services/AssistantAPI";
import { GAME_STATES, GameStateProvider, useGameState } from "../context/gameState";
import {
  canEnterEnvironment,
  getParentEnvironmentId,
  getSelectedElementTargetEnvironmentId,
  getSystemCurrentEnvironmentId,
  getSystemEnvironments,
  normalizeEnvironmentLoadStatesPayload,
  parseConditionChangedEvent,
} from "../utils/systemData";

import useWebGpuDebug from "./unityWebViewer/useWebGpuDebug";
import useUnityEventBindings from "./unityWebViewer/useUnityEventBindings";
import MobileFallback from "./unityWebViewer/MobileFallback";
import WebGpuFallback from "./unityWebViewer/WebGpuFallback";
import {
  getExperienceLoadMessage,
  VIEWER_BUILD_FAILURE,
  VIEWER_STARTUP_FAILURE,
  WEBGPU_UNAVAILABLE,
} from "../utils/customerMessages";
import StatusOverlays from "./unityWebViewer/StatusOverlays";
import UnityCanvas from "./unityWebViewer/UnityCanvas";
import ViewerGestureTutorial from "./unityWebViewer/ViewerGestureTutorial";
import { getUnityCacheControl } from "./unityWebViewer/cacheControl";
import {
  ANDROID_TOUCH_INPUT_MODE,
  getAndroidWebGpuFallbackDetail,
  isAndroidPlatform,
  isAndroidTouchDevice,
} from "./unityWebViewer/androidTouchInput";
import {
  getViewerActionPaddingX,
  getViewerPanelPaddingSx,
  viewerActionButtonLabelSx,
  viewerActionButtonSx,
  viewerCompactActionPaddingX,
  viewerGhostActionButtonSx,
  viewerPillActionButtonSx,
  viewerSecondaryActionButtonSx,
  viewerPanelSurfaceSx,
} from "./unityWebViewer/panelStyles";
import {
  LOCAL_UNITY_BUILD_PREFIX,
  PROD_UNITY_BUILD_MANIFEST_URL,
  STAGING_UNITY_BUILD_MANIFEST_URL,
} from "./unityWebViewer/constants";
import {
  GUIDE_AUDIO_LEVEL_REQUEST_INTERVAL_MS,
  GUIDE_STATE_PLACEHOLDER_TEXT,
  MICROPHONE_DENIED_GUIDE_CONTINUE_DELAY_MS,
  SHOW_GUIDE_MANAGER_PANEL,
  clampAudioLevel,
  getInsightIdFromPayload,
  getGuideDisplayUpdateFromPayload,
  getGuideAudioLevelRawFromPayload,
  getLastGuideStepId,
  guideStepIdsMatch,
  isGuideIdleForQuestions,
  isGuideStateTalking,
} from "./unityWebViewer/guideRuntime";
import {
  advanceViewerTutorial,
  getStoredViewerTutorialState,
  getViewerGestureTutorialStep,
  isViewerTutorialCompleted,
  persistViewerTutorialCompleted,
} from "./unityWebViewer/viewerTutorial";

const SHOW_SESSION_INSIGHTS_PANEL = true;
const FORCE_WEBGPU_FALLBACK = false;
const PLATFORM_SIGNUP_URL = "https://dashboard.10k.science/signup";
const DEFAULT_VIEWER_GUIDE_COMPLETION_MESSAGE =
  "You've completed this experience. Keep exploring here, or get more 10k Science content for your classroom.";
const GENERIC_GUIDE_COMPLETION_MESSAGE =
  "You've completed this experience. You can keep exploring or restart the guide.";
const GUIDE_START_ENVIRONMENT_CHANGE_TIMEOUT_MS = 12000;
const ZOOM_WAIT_LABEL = "Wait";
const CONTENT_REQUEST_PARAMS = {
  version: 6000,
  platform: "web",
  runtimePlatform: "web",
};
const PLATFORM_PROMO_THUMBNAILS = [
  {
    title: "CRISPR & Sickle Cell",
    src: "/featured-experiences/crispr-and-sickle-cell.png",
  },
  {
    title: "Trouble in Sunshine Creek",
    src: "/featured-experiences/trouble-in-sunshine-creek.png",
  },
  {
    title: "Snack Shack Training Experience",
    src: "/featured-experiences/snack-shack-training-experience.png",
  },
  {
    title: "Pain and Painkillers",
    src: "/featured-experiences/pain-and-painkillers.png",
  },
  {
    title: "Breathing in the Wildfire",
    src: "/featured-experiences/breathing-in-the-wildfire.png",
  },
  {
    title: "Seeing With Sound",
    src: "/featured-experiences/seeing-with-sound.png",
  },
];

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const LOCAL_UNITY_BUILD_CONFIG = {
  buildId: "local",
  version: "local",
  loaderUrl: `${LOCAL_UNITY_BUILD_PREFIX}.loader.js`,
  dataUrl: `${LOCAL_UNITY_BUILD_PREFIX}.data`,
  frameworkUrl: `${LOCAL_UNITY_BUILD_PREFIX}.framework.js`,
  codeUrl: `${LOCAL_UNITY_BUILD_PREFIX}.wasm`,
};

const isStagingViewerHost = (hostname) =>
  hostname === "viewer-staging.10k.science";

const normalizeUnityChannel = (channel) => {
  const normalizedChannel = channel?.trim().toLowerCase();
  if (normalizedChannel === "staging" || normalizedChannel === "stage") {
    return "staging";
  }

  if (normalizedChannel === "prod" || normalizedChannel === "production") {
    return "prod";
  }

  return "";
};

const getDefaultManifestUrlForChannel = (channel) =>
  channel === "staging"
    ? STAGING_UNITY_BUILD_MANIFEST_URL
    : PROD_UNITY_BUILD_MANIFEST_URL;

const getDefaultUnityBuildManifestUrl = (channelOverride = "") => {
  const configuredUrl = process.env.REACT_APP_UNITY_BUILD_MANIFEST_URL?.trim();
  if (configuredUrl) {
    return configuredUrl;
  }

  if (channelOverride) {
    return getDefaultManifestUrlForChannel(channelOverride);
  }

  const configuredChannel = normalizeUnityChannel(
    process.env.REACT_APP_UNITY_BUILD_CHANNEL,
  );
  if (configuredChannel) {
    return getDefaultManifestUrlForChannel(configuredChannel);
  }

  if (typeof window !== "undefined" && isStagingViewerHost(window.location.hostname)) {
    return getDefaultManifestUrlForChannel("staging");
  }

  return getDefaultManifestUrlForChannel("prod");
};

const getManifestSiblingUrl = (latestManifestUrl, segment, value) => {
  const encodedValue = encodeURIComponent(value);

  try {
    const url = new URL(latestManifestUrl, window.location.origin);
    if (!url.pathname.endsWith("/latest.json")) {
      return {
        errorMessage:
          "Version build overrides require a latest manifest URL ending in /latest.json.",
      };
    }

    url.pathname = url.pathname.replace("/latest.json", `/${segment}/${encodedValue}.json`);
    url.search = "";
    url.hash = "";
    return { manifestUrl: url.toString() };
  } catch (error) {
    return { errorMessage: `Invalid Unity build manifest URL: ${latestManifestUrl}` };
  }
};

const getSearchParamValue = (params, name) => {
  const exactValue = params.get(name)?.trim();
  if (exactValue) {
    return exactValue;
  }

  const normalizedName = name.toLowerCase();
  for (const [paramName, value] of params.entries()) {
    if (paramName.toLowerCase() === normalizedName) {
      const normalizedValue = value.trim();
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }

  return "";
};

const getUnityBuildManifestSelection = () => {
  if (typeof window === "undefined") {
    return { manifestUrl: "", showVersionOverlay: false };
  }

  const params = new URLSearchParams(window.location.search);
  const explicitManifestUrl =
    getSearchParamValue(params, "unityManifest") ||
    getSearchParamValue(params, "unityBuildManifest");
  if (explicitManifestUrl) {
    return { manifestUrl: explicitManifestUrl, showVersionOverlay: true };
  }

  const unityBuild = getSearchParamValue(params, "unityBuild");
  if (unityBuild?.toLowerCase() === "local") {
    return {
      manifestUrl: "",
      showVersionOverlay: true,
      versionOverlayLabel: "local",
    };
  }

  const channelOverride = normalizeUnityChannel(
    getSearchParamValue(params, "unityChannel"),
  );
  const latestManifestUrl = getDefaultUnityBuildManifestUrl(channelOverride);
  const version = getSearchParamValue(params, "unityVersion") || unityBuild;
  if (version) {
    if (!latestManifestUrl) {
      return {
        errorMessage:
          "Unity version override requires REACT_APP_UNITY_BUILD_MANIFEST_URL.",
        showVersionOverlay: true,
        versionOverlayLabel: version,
      };
    }

    return {
      ...getManifestSiblingUrl(latestManifestUrl, "versions", version),
      showVersionOverlay: true,
      versionOverlayLabel: version,
    };
  }

  const buildId = getSearchParamValue(params, "unityBuildId");
  if (buildId) {
    if (!latestManifestUrl) {
      return {
        errorMessage:
          "Unity build id override requires REACT_APP_UNITY_BUILD_MANIFEST_URL.",
        showVersionOverlay: true,
      };
    }

    return {
      ...getManifestSiblingUrl(latestManifestUrl, "builds", buildId),
      showVersionOverlay: true,
    };
  }

  return { manifestUrl: latestManifestUrl, showVersionOverlay: false };
};

const normalizeUnityBuildManifest = (manifest, manifestUrl) => {
  const unity = manifest?.unity;
  const missingFields = ["loaderUrl", "dataUrl", "frameworkUrl", "codeUrl"].filter(
    (field) => !unity?.[field],
  );

  if (missingFields.length > 0) {
    throw new Error(
      `Unity build manifest is missing ${missingFields.join(", ")}.`,
    );
  }

  return {
    buildId: manifest.buildId,
    version: manifest.version,
    manifestUrl,
    loaderUrl: unity.loaderUrl,
    dataUrl: unity.dataUrl,
    frameworkUrl: unity.frameworkUrl,
    codeUrl: unity.codeUrl,
  };
};

const mergeSystemData = (baseSystemData, incomingSystemData) => {
  if (!baseSystemData) {
    return incomingSystemData ?? null;
  }

  if (!incomingSystemData) {
    return baseSystemData;
  }

  return {
    ...baseSystemData,
    ...incomingSystemData,
    environments:
      incomingSystemData.environments ?? baseSystemData.environments,
    json: incomingSystemData.json ?? baseSystemData.json,
    webMap: incomingSystemData.webMap ?? baseSystemData.webMap,
  };
};

const normalizeElementSelectedPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  if (!payload.elementData || typeof payload.elementData !== "object") {
    return payload;
  }

  return {
    ...payload.elementData,
    dispatchedName: payload.name,
    dispatchedDescription: payload.description,
  };
};

const getElementDisplayNameValue = (elementData) =>
  elementData?.dispatchedName ||
  elementData?.labelData?.displayName ||
  elementData?.labelData?.name ||
  elementData?.name ||
  elementData?.id ||
  "";

const getElementDescriptionValue = (elementData) =>
  elementData?.dispatchedDescription || elementData?.description || "";

const getEnvironmentDisplayNameValue = (environmentData) =>
  environmentData?.environmentName || environmentData?.displayName || environmentData?.name || "";

const getDiagnosticErrorMessage = (error) => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message || "";
  }

  return String(error);
};

const isMicrophonePermissionError = (error) =>
  ["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(
    error?.name,
  );

const getViewerStartupFailure = (webGpuState, initialisationError) => {
  if (FORCE_WEBGPU_FALLBACK) {
    return WEBGPU_UNAVAILABLE;
  }

  if (webGpuState.status === "unsupported") {
    return WEBGPU_UNAVAILABLE;
  }

  if (!initialisationError) {
    return null;
  }

  const detail = getDiagnosticErrorMessage(initialisationError);
  const normalizedDetail = detail.toLowerCase();
  const looksLikeWebGpuFailure =
    normalizedDetail.includes("webgpu") ||
    normalizedDetail.includes("gpu adapter") ||
    normalizedDetail.includes("adapter not found");

  if (looksLikeWebGpuFailure) {
    return WEBGPU_UNAVAILABLE;
  }

  return VIEWER_STARTUP_FAILURE;
};

const buildViewerQuestionPayload = ({
  experienceData,
  systemData,
  currentEnvironment,
  currentElement,
  visitedEnvironmentNames,
  guideState,
  guideStateText,
  guideInstructionText,
}) => {
  const experienceTitle = experienceData?.name || "";
  const systemName = systemData?.name || "";
  const environmentName = getEnvironmentDisplayNameValue(currentEnvironment);
  const elementName = getElementDisplayNameValue(currentElement);
  const elementDescription = getElementDescriptionValue(currentElement);
  const systemEnvironments = getSystemEnvironments(systemData);
  const matchingEnvironment = currentEnvironment?.id
    ? systemEnvironments.find(
        (environment) => environment?.id === currentEnvironment.id,
      )
    : null;
  const currentEnvironmentDescription =
    currentEnvironment?.description || matchingEnvironment?.description || "";
  const availableEnvironmentNames = systemEnvironments
    .map((environment) => getEnvironmentDisplayNameValue(environment))
    .filter(Boolean);
  const normalizedVisitedEnvironmentNames = Array.isArray(visitedEnvironmentNames)
    ? visitedEnvironmentNames.filter(Boolean)
    : [];
  const guideMessage =
    (typeof guideState?.script === "string" && guideState.script.trim()) ||
    (typeof guideState?.data === "string" && guideState.data.trim()) ||
    (typeof guideStateText === "string" && guideStateText.trim()) ||
    "";
  const activeGuideInstruction =
    typeof guideInstructionText === "string" ? guideInstructionText.trim() : "";

  const contextBlocks = [
    experienceTitle && {
      id: "experience-title",
      label: "Experience",
      kind: "context",
      priority: 0,
      text: `Experience title: ${experienceTitle}`,
    },
    systemName && {
      id: "experience-topic",
      label: "Experience topic",
      kind: "context",
      priority: 1,
      text: `Experience topic: ${systemName}`,
    },
    environmentName && {
      id: "environment-name",
      label: "Current environment",
      kind: "context",
      priority: 2,
      text: `Current environment: ${environmentName}`,
    },
    currentEnvironmentDescription && {
      id: "environment-description",
      label: "Current environment description",
      kind: "context",
      priority: 3,
      text: currentEnvironmentDescription,
    },
    elementName && {
      id: "element-name",
      label: "Current element",
      kind: "context",
      priority: 4,
      text: `Current element: ${elementName}`,
    },
    elementDescription && {
      id: "element-description",
      label: "Element description",
      kind: "context",
      priority: 5,
      text: elementDescription,
    },
    availableEnvironmentNames.length > 0 && {
      id: "available-environments",
      label: "Available environments",
      kind: "context",
      priority: 6,
      text: `Available environments in this experience: ${availableEnvironmentNames.join(", ")}`,
    },
    normalizedVisitedEnvironmentNames.length > 0 && {
      id: "visited-environments",
      label: "Visited environments",
      kind: "context",
      priority: 7,
      text: `Visited environments so far: ${normalizedVisitedEnvironmentNames.join(", ")}`,
    },
    guideMessage && {
      id: "guide-message",
      label: "Guide message",
      kind: "context",
      priority: 8,
      text: guideMessage,
    },
    activeGuideInstruction && {
      id: "guide-instruction",
      label: "Guide instruction",
      kind: "context",
      priority: 9,
      text: activeGuideInstruction,
    },
  ].filter(Boolean);

  return {
    systemId: experienceData?.systemId ?? null,
    guideId: experienceData?.guideId ?? null,
    context: {
      experienceTitle,
      systemName,
      environmentName,
      elementName,
      availableEnvironmentNames,
      visitedEnvironmentNames: normalizedVisitedEnvironmentNames,
      guideState: guideState?.guideState || null,
      guideMessage,
      guideInstruction: activeGuideInstruction,
      contextBlocks,
    },
    contextBlocks,
  };
};

const getExperienceRequestPath = (experienceId, experienceRequestMode) =>
  experienceRequestMode === "viewer"
    ? `viewer-experiences/${experienceId}`
    : `experiences/${experienceId}`;

const getGuideRequestPath = (guideId) => `guides/${encodeURIComponent(guideId)}`;

const getInsightIdFromGuideStatePayload = (payload) => {
  if (typeof payload !== "string") {
    return getInsightIdFromPayload(payload);
  }

  const trimmedPayload = payload.trim();
  if (!trimmedPayload.startsWith("{") && !trimmedPayload.startsWith("[")) {
    return "";
  }

  return getInsightIdFromPayload(trimmedPayload);
};

const getExperienceRequestConfig = (experienceRequestMode) =>
  experienceRequestMode === "viewer" ? { authMode: "viewer" } : {};

const getRuntimeExperiencesRequestConfig = (experienceRequestMode) =>
  experienceRequestMode === "viewer"
    ? { authMode: "viewer" }
    : experienceRequestMode === "runtime"
      ? { authMode: "runtime" }
      : {};

const USER_DATA_HANDSHAKE_TIMEOUT_MS = 10_000;

const getUnityBuildVersionOverlayLabel = (buildVersion, fallbackVersion) => {
  const version = buildVersion ?? fallbackVersion ?? "";
  return String(version).trim();
};

const EMPTY_EDGE_HOVER = {
  left: false,
  right: false,
  top: false,
  bottom: false,
};

const ZOOM_BUTTON_SIZE_DESKTOP = 64;
const ZOOM_BUTTON_SIZE_PHONE = 56;
const FOCUSED_INFO_BUTTON_WIDTH_DESKTOP = 164;
const FOCUSED_INFO_BUTTON_WIDTH_PHONE = 150;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getFinitePoint = (point) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

const getClampedFocusControlPosition = (point, isPhone) => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 0;
  const controlRadius = (isPhone ? ZOOM_BUTTON_SIZE_PHONE : ZOOM_BUTTON_SIZE_DESKTOP) / 2;
  const edgePadding = controlRadius + (isPhone ? 10 : 14);
  const finitePoint = getFinitePoint(point);

  if (!viewportWidth || !viewportHeight) {
    return finitePoint;
  }

  const fallbackPoint = {
    x: viewportWidth / 2,
    y: clamp(viewportHeight * 0.28, edgePadding, viewportHeight - edgePadding),
  };
  const sourcePoint = finitePoint || fallbackPoint;

  return {
    x: clamp(sourcePoint.x, edgePadding, viewportWidth - edgePadding),
    y: clamp(sourcePoint.y, edgePadding, viewportHeight - edgePadding),
  };
};

const getFocusedInfoButtonLayout = (zoomPosition, zoomButtonSize, isPhone) => {
  const finiteZoomPosition = getFinitePoint(zoomPosition);
  if (!finiteZoomPosition) {
    return null;
  }

  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 0;
  const buttonWidth = isPhone
    ? FOCUSED_INFO_BUTTON_WIDTH_PHONE
    : FOCUSED_INFO_BUTTON_WIDTH_DESKTOP;
  const buttonHeight = isPhone ? 36 : 38;
  const edgePadding = isPhone ? 10 : 14;
  const gap = isPhone ? 12 : 14;
  const rightX = finiteZoomPosition.x + zoomButtonSize / 2 + gap;
  const leftX = finiteZoomPosition.x - zoomButtonSize / 2 - gap - buttonWidth;

  if (!viewportWidth || !viewportHeight) {
    return {
      x: rightX,
      y: finiteZoomPosition.y,
      width: buttonWidth,
      transform: "translateY(-50%)",
    };
  }

  const minX = edgePadding;
  const maxX = Math.max(minX, viewportWidth - edgePadding - buttonWidth);
  const minY = edgePadding + buttonHeight / 2;
  const maxY = Math.max(minY, viewportHeight - edgePadding - buttonHeight / 2);
  const fitsRight = rightX <= maxX;
  const x = fitsRight ? rightX : clamp(leftX, minX, maxX);

  return {
    x,
    y: clamp(finiteZoomPosition.y, minY, maxY),
    width: buttonWidth,
    transform: "translateY(-50%)",
  };
};

const getClientPositionInElement = (element, clientX, clientY) => {
  const bounds = element.getBoundingClientRect();
  return {
    x: clientX - bounds.left,
    y: clientY - bounds.top,
  };
};

const getPointerPositionInElement = (event) =>
  getClientPositionInElement(event.currentTarget, event.clientX, event.clientY);

const stopOverlayPointerEvent = (event) => {
  event?.stopPropagation?.();
  const nativeEvent = event?.nativeEvent || event;
  nativeEvent?.stopImmediatePropagation?.();
  nativeEvent?.stopPropagation?.();
};

const blockOverlayActionEvent = (event) => {
  event?.preventDefault?.();
  stopOverlayPointerEvent(event);
};

const forwardWheelEventToUnityCanvas = (event) => {
  const nativeEvent = event.nativeEvent || event;
  const canvas =
    document.querySelector(".unity-canvas-container canvas") ||
    document.querySelector("canvas");

  if (!canvas || typeof canvas.dispatchEvent !== "function") {
    return;
  }

  const forwardedEvent = new WheelEvent("wheel", {
    bubbles: true,
    cancelable: true,
    view: window,
    detail: nativeEvent.detail,
    screenX: nativeEvent.screenX,
    screenY: nativeEvent.screenY,
    clientX: nativeEvent.clientX,
    clientY: nativeEvent.clientY,
    ctrlKey: nativeEvent.ctrlKey,
    shiftKey: nativeEvent.shiftKey,
    altKey: nativeEvent.altKey,
    metaKey: nativeEvent.metaKey,
    button: nativeEvent.button,
    buttons: nativeEvent.buttons,
    deltaX: nativeEvent.deltaX,
    deltaY: nativeEvent.deltaY,
    deltaZ: nativeEvent.deltaZ,
    deltaMode: nativeEvent.deltaMode,
  });

  canvas.dispatchEvent(forwardedEvent);
  event.preventDefault();
  event.stopPropagation();
};

const ZoomInIcon = () => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M21 21L16.65 16.65M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M10.5 7.5V13.5M7.5 10.5H13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </SvgIcon>
);

const ZoomOutIcon = () => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M21 21L16.65 16.65M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M7.5 10.5H13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </SvgIcon>
);

const ExploreObjectIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M5 12H18M13 7L18 12L13 17"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.4"
    />
  </SvgIcon>
);

const BackArrowIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M19 12H6M11 7L6 12L11 17"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.4"
    />
  </SvgIcon>
);

const DoneCheckIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M5 12.5L9.5 17L19 7"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
    />
  </SvgIcon>
);

const ExternalLinkIcon = (props) => (
  <SvgIcon
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path
      d="M7 17L17 7M10 7H17V14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
    />
  </SvgIcon>
);

const ZoomControlButton = ({
  ariaLabel,
  children,
  disabled = false,
  disabledTitle = ZOOM_WAIT_LABEL,
  onClick,
  onWheel,
  size = ZOOM_BUTTON_SIZE_DESKTOP,
  testId,
  title,
}) => (
  <Tooltip
    arrow
    title={disabled ? disabledTitle : title}
    slotProps={{
      tooltip: {
        sx: {
          px: 1.35,
          py: 0.9,
          borderRadius: "8px",
          backgroundColor: "rgba(4, 12, 18, 0.96)",
          boxShadow: "0 10px 24px rgba(0, 0, 0, 0.34)",
          color: "#ffffff",
          fontSize: "0.82rem",
          fontWeight: 700,
          lineHeight: 1.2,
        },
      },
      arrow: {
        sx: {
          color: "rgba(4, 12, 18, 0.96)",
        },
      },
    }}
  >
    <IconButton
      aria-label={ariaLabel}
      aria-disabled={disabled ? "true" : undefined}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        if (disabled) {
          event.preventDefault();
          return;
        }

        onClick();
      }}
      onMouseDown={stopOverlayPointerEvent}
      onMouseUp={stopOverlayPointerEvent}
      onTouchStart={stopOverlayPointerEvent}
      onTouchEnd={stopOverlayPointerEvent}
      onWheel={onWheel}
      size="large"
      sx={{
        ...viewerActionButtonSx,
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        p: 0,
        borderRadius: "50%",
        overflow: "hidden",
        "& .MuiSvgIcon-root": {
          ...viewerActionButtonSx["& .MuiSvgIcon-root, & svg"],
          fontSize: Math.round(size * 0.5),
        },
        "& .zoom-button-default-content": {
          display: "inline-flex",
          opacity: 1,
          transition: "opacity 120ms ease",
        },
        "& .zoom-button-wait-content": {
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "1px",
          color: "inherit",
          fontSize: Math.max(10, Math.round(size * 0.16)),
          fontWeight: 900,
          lineHeight: 1,
          opacity: 0,
          pointerEvents: "none",
          textTransform: "uppercase",
          transition: "opacity 120ms ease",
        },
        ...(disabled
          ? {
              "&:hover .zoom-button-default-content, &:focus-visible .zoom-button-default-content":
                {
                  opacity: 0,
                },
              "&:hover .zoom-button-wait-content, &:focus-visible .zoom-button-wait-content":
                {
                  opacity: 1,
                },
            }
          : {}),
      }}
    >
      <Box component="span" className="zoom-button-default-content">
        {children}
      </Box>
      {disabled && (
        <Box
          component="span"
          aria-hidden="true"
          className="zoom-button-wait-content"
        >
          <CircularProgress
            color="inherit"
            size={Math.max(14, Math.round(size * 0.25))}
            thickness={5}
          />
          <Box component="span">{disabledTitle}</Box>
        </Box>
      )}
    </IconButton>
  </Tooltip>
);

const GuideCompletionPopup = ({
  isPhone,
  message,
  onPrimaryAction,
  onSecondaryAction,
  open,
  primaryActionLabel = "Keep exploring",
  secondaryActionHref = "",
  secondaryActionLabel = "",
  title = "Experience complete",
}) => {
  if (!open) {
    return null;
  }

  const stopCompletionPopupEvent = (event) => {
    event.stopPropagation();
    if (
      (event.type === "wheel" || event.type === "touchmove") &&
      event.cancelable
    ) {
      event.preventDefault();
    }
  };
  const hasSecondaryAction = Boolean(
    secondaryActionLabel && (secondaryActionHref || onSecondaryAction),
  );
  const primaryButtonSx = {
    ...viewerPillActionButtonSx,
    px: getViewerActionPaddingX(isPhone),
    font: "inherit",
    fontSize: "14px",
  };
  const secondaryButtonSx = {
    ...viewerSecondaryActionButtonSx,
    px: getViewerActionPaddingX(isPhone),
    font: "inherit",
    fontSize: "14px",
    background: "rgba(10, 20, 28, 0.42)",
    color: "#f2fffc",
    "&:hover": {
      background: "rgba(161, 241, 224, 0.14)",
      borderColor: "rgba(210, 255, 249, 0.42)",
      boxShadow: "none",
    },
  };

  return (
    <Box
      aria-labelledby="guide-completion-title"
      aria-modal="false"
      role="dialog"
      onClick={stopCompletionPopupEvent}
      onMouseDown={stopCompletionPopupEvent}
      onMouseMove={stopCompletionPopupEvent}
      onMouseUp={stopCompletionPopupEvent}
      onPointerDown={stopCompletionPopupEvent}
      onPointerMove={stopCompletionPopupEvent}
      onPointerUp={stopCompletionPopupEvent}
      onTouchEnd={stopCompletionPopupEvent}
      onTouchMove={stopCompletionPopupEvent}
      onTouchStart={stopCompletionPopupEvent}
      onWheel={stopCompletionPopupEvent}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: "var(--start-overlay-chrome-z-index, 2010)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          width: "min(92vw, 430px)",
          boxSizing: "border-box",
          px: isPhone ? 2.4 : 3,
          py: isPhone ? 2.25 : 2.75,
          textAlign: "center",
          ...viewerPanelSurfaceSx,
          borderRadius: "var(--radius-lg, 14px)",
          border: "1px solid rgba(161, 241, 224, 0.28)",
          boxShadow:
            "0 0 32px rgba(89, 218, 230, 0.18), 0 24px 72px rgba(0, 0, 0, 0.42)",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "rgba(161, 241, 224, 0.94)",
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Experience complete
        </Typography>
        <Typography
          id="guide-completion-title"
          variant="h5"
          sx={{
            mt: 0.75,
            color: "#f2fffc",
            fontSize: isPhone ? "1.25rem" : "1.45rem",
            fontWeight: 700,
            lineHeight: 1.16,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 1,
            color: "rgba(242, 255, 252, 0.82)",
            fontSize: isPhone ? "0.92rem" : "0.98rem",
            lineHeight: 1.45,
          }}
        >
          {message}
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: isPhone ? "column" : "row",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onPrimaryAction}
            sx={primaryButtonSx}
          >
            <Box
              component="span"
              className="control-label"
              sx={{
                ...viewerActionButtonLabelSx,
                fontSize: "14px",
                fontWeight: "var(--viewer-action-font-weight, 700)",
              }}
            >
              {primaryActionLabel}
            </Box>
          </Box>
          {hasSecondaryAction && (
            <Box
              component={secondaryActionHref ? "a" : "button"}
              type={secondaryActionHref ? undefined : "button"}
              href={secondaryActionHref || undefined}
              target={secondaryActionHref ? "_blank" : undefined}
              rel={secondaryActionHref ? "noopener noreferrer" : undefined}
              onClick={onSecondaryAction}
              sx={secondaryButtonSx}
            >
              <Box
                component="span"
                className="control-label"
                sx={{
                  ...viewerActionButtonLabelSx,
                  fontSize: "14px",
                  fontWeight: "var(--viewer-action-font-weight, 700)",
                }}
              >
                {secondaryActionLabel}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const edgeHoverStatesMatch = (firstHover, secondHover) =>
  firstHover.left === secondHover.left &&
  firstHover.right === secondHover.right &&
  firstHover.top === secondHover.top &&
  firstHover.bottom === secondHover.bottom;

const UnityWebViewerContent = ({
  experienceId,
  embed,
  experienceRequestMode = "teacher",
  onExperienceAccessDenied,
  userData,
  completionCtaVariant = "generic",
  showPlatformSignupCta = false,
  unityBuildConfig,
  unityBuildVersionOverlay,
}) => {
  const voiceRecorderRef = useRef(null);
  const [currentElement, setCurrentElement] = useState(null);
  const [lastUnitySelectedElement, setLastUnitySelectedElement] =
    useState(null);
  const [systemData, setSystemData] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [focusedPanelElementLocked, setFocusedPanelElementLocked] =
    useState(null);
  const [focusedPanelPositionLocked, setFocusedPanelPositionLocked] =
    useState(null);
  const [focusedZoomReadyElementId, setFocusedZoomReadyElementId] =
    useState("");
  const [elementInformationElement, setElementInformationElement] =
    useState(null);
  const [elementInformationVisible, setElementInformationVisible] =
    useState(false);
  const [labelPosition, setLabelPosition] = useState(null);
  const [pointerInGuideArea, setPointerInGuideArea] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [environmentLoadStates, setEnvironmentLoadStates] = useState(null);
  const [environmentConditionValues, setEnvironmentConditionValues] =
    useState({});
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [experienceData, setExperienceData] = useState(null);
  const [guideData, setGuideData] = useState(null);
  const [layoutOverridesAll, setLayoutOverridesAll] = useState(null);
  const [layoutOverrides, setLayoutOverrides] = useState(null);
  const [cursorStyle, setCursorStyle] = useState("default");
  const focusTimeoutRef = useRef(null);
  const latestMousePositionRef = useRef({ x: 0, y: 0 });
  const hasObservedMousePositionRef = useRef(false);
  const skipNextFocusPlacementRef = useRef(false);
  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [environmentLookup, setEnvironmentLookup] = useState({});
  const [visitedEnvironmentNames, setVisitedEnvironmentNames] = useState([]);
  const [elementLookup, setElementLookup] = useState({});
  const [localEvents, setLocalEvents] = useState([]);
  const currentElementRef = useRef(null);
  const elementInformationVisibleRef = useRef(false);
  const elementInformationModePendingRef = useRef(false);
  const elementInformationModeConfirmationTimeoutRef = useRef(null);
  const guideStartPendingRef = useRef(false);
  const pendingGuideStartEnvironmentChangeRef = useRef(null);
  const [viewerTutorial, setViewerTutorial] = useState(() =>
    getStoredViewerTutorialState(),
  );
  const viewerTutorialCompletedRef = useRef(
    isViewerTutorialCompleted(viewerTutorial),
  );
  const [platformSignupPanelOpen, setPlatformSignupPanelOpen] =
    useState(false);
  const [, setEdgeHover] = useState(EMPTY_EDGE_HOVER);
  const edgeHoverRef = useRef(EMPTY_EDGE_HOVER);
  const edgeHoverPendingRef = useRef(EMPTY_EDGE_HOVER);
  const edgeHoverTimeoutRef = useRef(null);

  const [guideState, setGuideState] = useState(null);
  const [guideStateText, setGuideStateText] = useState(
    GUIDE_STATE_PLACEHOLDER_TEXT,
  );
  const [guideInstructionText, setGuideInstructionText] = useState("");
  const [guideAudioLevel, setGuideAudioLevel] = useState(0);
  const [guideCompletionPopupOpen, setGuideCompletionPopupOpen] =
    useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);
  const [highlightTimelineButton, setHighlightTimelineButton] = useState(false);
  const [experienceRequestError, setExperienceRequestError] = useState(null);
  const [unityReadyToLoadExperience, setUnityReadyToLoadExperience] =
    useState(false);
  const [userDataDispatchedToUnity, setUserDataDispatchedToUnity] =
    useState(false);
  const [unityUserDataApplied, setUnityUserDataApplied] = useState(false);
  const [experienceDispatchedToUnity, setExperienceDispatchedToUnity] =
    useState(false);
  const [webGpuState, setWebGpuState] = useState({
    status:
      typeof navigator === "undefined" || "gpu" in navigator
        ? "checking"
        : "unsupported",
    detail:
      typeof navigator === "undefined" || "gpu" in navigator
        ? ""
        : "The WebGPU API is not available in this browser.",
  });

  const [isRecording, setIsRecording] = useState(false);
  const [questionState, setQuestionState] = useState("idle");
  const [questionDisplayText, setQuestionDisplayText] = useState("");
  const questionIdRef = useRef(null);
  const questionPresignedUrlRef = useRef(null);
  const questionRecordingFormatRef = useRef(null);
  const skipNextUnityAudioUploadRef = useRef(false);
  const lastExchangeRef = useRef(null);
  const questionAudioRef = useRef(null);
  const questionResetTimeoutRef = useRef(null);
  const micDeniedGuideContinueTimeoutRef = useRef(null);
  const activeGuideInsightIdRef = useRef("");
  const completedGuideInsightIdRef = useRef("");
  const guideCompletionPopupShownRef = useRef(false);
  // null = not yet chosen, true = guide on, false = opted out
  const [guideEnabled, setGuideEnabled] = useState(null);
  const [micPromptEnabled, setMicPromptEnabled] = useState(false);
  const [micPromptIntent, setMicPromptIntent] = useState(null);
  const [micPromptShowDeniedInstructions, setMicPromptShowDeniedInstructions] =
    useState(false);
  const [microphonePermissionDenied, setMicrophonePermissionDenied] =
    useState(false);

  const { currentGameState, setGameState, isCurrentGameState } = useGameState();
  const isElementFocusedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementFocused,
  ]);
  const isElementSelectedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementSelected,
  ]);
  const shouldKeepFocusedElementUi = Boolean(
    isElementFocusedGameState || focusedPanelElementLocked,
  );

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isLandscapeViewport = useMediaQuery("(orientation: landscape)");
  const isPhonePortrait = isPhone && !isLandscapeViewport;
  const userAgent =
    typeof navigator === "undefined" ? "" : navigator.userAgent;
  const isIpadOsDevice =
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1;
  const isAppleMobileDevice =
    /iPhone|iPad|iPod/i.test(userAgent) || isIpadOsDevice;
  const androidTouchDevice = isAndroidTouchDevice();
  const androidPlatform = isAndroidPlatform();
  const isTouchTutorialDevice =
    isPhone ||
    isTablet ||
    (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);
  const overlayWidth = isPhone ? "92vw" : isTablet ? "70vw" : "540px";
  const envLabelBottom = isPhone ? "100px" : "150px";
  const zoomButtonSize = isPhone
    ? ZOOM_BUTTON_SIZE_PHONE
    : ZOOM_BUTTON_SIZE_DESKTOP;
  const rightPanelInset =
    "var(--panel-horizontal-offset, var(--panel-left-offset, 20px))";
  const rightPanelRight = `calc(env(safe-area-inset-right, 0px) + ${rightPanelInset})`;
  const rightPanelBottom = `calc(env(safe-area-inset-bottom, 0px) + ${rightPanelInset})`;
  const rightPanelTopViewport = `calc(env(safe-area-inset-top, 0px) + ${rightPanelInset})`;
  const rightPanelWidth = "var(--panel-width-standard, 250px)";
  const rightPanelMaxWidth = `calc(100vw - ${rightPanelInset} - ${rightPanelInset})`;
  const zoomOutButtonBottomOffset = isPhonePortrait
    ? "var(--viewer-zoom-out-bottom-offset-phone-portrait, 112px)"
    : rightPanelInset;
  const zoomOutButtonBottom = `calc(env(safe-area-inset-bottom, 0px) + ${zoomOutButtonBottomOffset})`;
  const guideStackRightInset = isPhonePortrait
    ? "var(--guide-stack-right-offset-phone-portrait, -8px)"
    : rightPanelInset;
  const guideStackRight = `calc(env(safe-area-inset-right, 0px) + ${guideStackRightInset})`;
  const guideStackWidth = isPhonePortrait
    ? "var(--guide-stack-width-phone-portrait, 220px)"
    : rightPanelWidth;
  const guideStackMaxWidth = isPhonePortrait
    ? "calc(100vw - var(--panel-horizontal-offset, 20px))"
    : rightPanelMaxWidth;

  useWebGpuDebug();

  const {
    unityProvider,
    sendMessage,
    addEventListener,
    removeEventListener,
    loadingProgression,
    isLoaded,
    initialisationError,
  } = useUnityContext({
    loaderUrl: unityBuildConfig.loaderUrl,
    dataUrl: unityBuildConfig.dataUrl,
    frameworkUrl: unityBuildConfig.frameworkUrl,
    codeUrl: unityBuildConfig.codeUrl,
    cacheControl: getUnityCacheControl,
  });

  const playerOffset = 0;
  const dragStart = useRef(null);
  const touchDragStartRef = useRef(null);

  const sendMessageToUnity = useCallback(
    (gameObject, methodName, value) => {
      if (typeof value === "undefined") {
        sendMessage(gameObject, methodName);
        return;
      }

      sendMessage(gameObject, methodName, value);
    },
    [sendMessage],
  );

  const sendPinchGestureToUnity = useCallback(
    (payload) => {
      sendMessageToUnity(
        "EventDispatcher",
        "ApplyPinchGesture",
        JSON.stringify(payload),
      );
    },
    [sendMessageToUnity],
  );

  const clearPendingGuideStartEnvironmentChange = useCallback((error) => {
    const pending = pendingGuideStartEnvironmentChangeRef.current;
    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    pendingGuideStartEnvironmentChangeRef.current = null;

    if (error) {
      pending.reject(error);
    }
  }, []);

  const waitForGuideStartEnvironmentChanged = useCallback(
    (environmentId) => {
      const targetEnvironmentId = String(environmentId || "").trim();
      if (!targetEnvironmentId || currentEnvironment?.id === targetEnvironmentId) {
        return Promise.resolve();
      }

      clearPendingGuideStartEnvironmentChange(
        new Error("A newer guide start environment change was requested."),
      );

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          if (
            pendingGuideStartEnvironmentChangeRef.current?.environmentId ===
            targetEnvironmentId
          ) {
            pendingGuideStartEnvironmentChangeRef.current = null;
          }
          reject(
            new Error(
              "The viewer did not confirm the guide's opening environment in time.",
            ),
          );
        }, GUIDE_START_ENVIRONMENT_CHANGE_TIMEOUT_MS);

        pendingGuideStartEnvironmentChangeRef.current = {
          environmentId: targetEnvironmentId,
          reject,
          resolve: () => {
            clearTimeout(timeoutId);
            pendingGuideStartEnvironmentChangeRef.current = null;
            resolve();
          },
          timeoutId,
        };
      });
    },
    [clearPendingGuideStartEnvironmentChange, currentEnvironment?.id],
  );

  useEffect(
    () => () => {
      clearPendingGuideStartEnvironmentChange(
        new Error("Guide start environment change was cancelled."),
      );
    },
    [clearPendingGuideStartEnvironmentChange],
  );

  const advanceTutorial = useCallback((eventName) => {
    setViewerTutorial((currentTutorial) => {
      const nextTutorial = advanceViewerTutorial(currentTutorial, eventName);
      return nextTutorial === currentTutorial ? currentTutorial : nextTutorial;
    });
  }, []);

  useEffect(() => {
    if (
      viewerTutorialCompletedRef.current ||
      !isViewerTutorialCompleted(viewerTutorial)
    ) {
      return;
    }

    viewerTutorialCompletedRef.current = true;
    persistViewerTutorialCompleted();
  }, [viewerTutorial]);

  const setElementInformationVisibleState = useCallback((visible) => {
    elementInformationVisibleRef.current = visible;
    setElementInformationVisible(visible);
  }, []);

  const clearElementInformationModeConfirmationTimeout = useCallback(() => {
    if (elementInformationModeConfirmationTimeoutRef.current) {
      clearTimeout(elementInformationModeConfirmationTimeoutRef.current);
      elementInformationModeConfirmationTimeoutRef.current = null;
    }
  }, []);

  const clearElementInformationModeState = useCallback(() => {
    clearElementInformationModeConfirmationTimeout();
    elementInformationModePendingRef.current = false;
    setElementInformationVisibleState(false);
    setElementInformationElement(null);
  }, [
    clearElementInformationModeConfirmationTimeout,
    setElementInformationVisibleState,
  ]);

  useEffect(
    () => () => {
      clearElementInformationModeConfirmationTimeout();
    },
    [clearElementInformationModeConfirmationTimeout],
  );

  const clearMicDeniedGuideContinueTimeout = useCallback(() => {
    if (micDeniedGuideContinueTimeoutRef.current) {
      clearTimeout(micDeniedGuideContinueTimeoutRef.current);
      micDeniedGuideContinueTimeoutRef.current = null;
    }
  }, []);

  const continueGuideAfterMicrophoneDenial = useCallback(() => {
    clearMicDeniedGuideContinueTimeout();

    micDeniedGuideContinueTimeoutRef.current = setTimeout(() => {
      micDeniedGuideContinueTimeoutRef.current = null;
      skipNextUnityAudioUploadRef.current = true;
      sendMessageToUnity("EventDispatcher", "RecordingStopped");
    }, MICROPHONE_DENIED_GUIDE_CONTINUE_DELAY_MS);
  }, [clearMicDeniedGuideContinueTimeout, sendMessageToUnity]);

  const resetGuideCompletionTracking = useCallback(() => {
    activeGuideInsightIdRef.current = "";
    completedGuideInsightIdRef.current = "";
    guideCompletionPopupShownRef.current = false;
    setGuideCompletionPopupOpen(false);
  }, []);

  const dismissGuideCompletionPopup = useCallback((event) => {
    event?.stopPropagation?.();
    setGuideCompletionPopupOpen(false);
  }, []);

  const dismissGuideCompletionPopupForLink = useCallback((event) => {
    event?.stopPropagation?.();
    setGuideCompletionPopupOpen(false);
  }, []);

  const getElementTargetEnvironmentId = useCallback(
    (elementData) =>
      getSelectedElementTargetEnvironmentId({
        systemData,
        currentEnvironmentId: currentEnvironment?.id,
        elementData,
      }),
    [currentEnvironment?.id, systemData],
  );

  const elementHasTargetEnvironment = useCallback(
    (elementData) => Boolean(getElementTargetEnvironmentId(elementData)),
    [getElementTargetEnvironmentId],
  );

  const getEnvironmentNavigationLocked = useCallback(
    (environmentId) =>
      Boolean(
        environmentId &&
          environmentLoadStates &&
          !canEnterEnvironment(environmentLoadStates, environmentId),
      ),
    [environmentLoadStates],
  );

  const handleElementSelected = useCallback(
    (elementData) => {
      const parsedPayload =
        typeof elementData === "string" ? JSON.parse(elementData) : elementData;
      const parsedElement = normalizeElementSelectedPayload(parsedPayload);
      setLastUnitySelectedElement(parsedElement);
      setCurrentElement(parsedElement);
      currentElementRef.current = parsedElement;

      if (parsedElement?.id) {
        const elementName = getElementDisplayNameValue(parsedElement);

        setElementLookup((prev) => ({
          ...prev,
          [parsedElement.id]: { name: elementName },
        }));
      }
    },
    [],
  );

  const handleInsightStarted = useCallback((insightData) => {
    const insightId = getInsightIdFromPayload(insightData);
    if (insightId) {
      activeGuideInsightIdRef.current = insightId;
      completedGuideInsightIdRef.current = "";
    }
  }, []);

  const handleGameStateChanged = useCallback(
    (gameStateName) => {
      console.log("🎮 Game state changed:", gameStateName);
      setGameState(gameStateName);

      if (
        gameStateName === "ViewModeElementFocused" &&
        currentElementRef.current?.id
      ) {
        const elem = currentElementRef.current;
        const elementName = getElementDisplayNameValue(elem);
        advanceTutorial("elementSelected");

        setLocalEvents((prev) => {
          const lastElemEvent = [...prev]
            .reverse()
            .find((e) => e.pk?.startsWith("ElementExpanded#"));
          if (lastElemEvent && lastElemEvent.value === elem.id) {
            return prev;
          }
          return [
            ...prev,
            {
              pk: `ElementExpanded#${elem.id}`,
              sk: new Date().toISOString(),
              value: elem.id,
              elementName: elementName,
            },
          ];
        });
      }

      if (gameStateName === GAME_STATES.ViewModeElementInformation) {
        elementInformationModePendingRef.current = false;
        clearElementInformationModeConfirmationTimeout();
        setElementInformationVisibleState(true);
        setElementInformationElement(
          (previousElement) =>
            (currentElementRef.current?.id && currentElementRef.current) ||
            previousElement,
        );
      } else if (!elementInformationModePendingRef.current) {
        setElementInformationVisibleState(false);
        setElementInformationElement(null);
      }
    },
    [
      clearElementInformationModeConfirmationTimeout,
      advanceTutorial,
      setElementInformationVisibleState,
      setGameState,
    ],
  );

  const handleAssessmentComplete = useCallback((assessmentId) => {
    setTimeout(() => {
      ExperiencesAPI.get(
        `assessments/${assessmentId}`,
        getRuntimeExperiencesRequestConfig(experienceRequestMode),
      )
        .catch((error) => {
          console.error("Failed to fetch assessment data", error);
        });
    }, 3000);
  }, [experienceRequestMode]);

  const handleInstructionVisibilityChanged = useCallback(() => {}, []);

  const handleEnvironmentChanged = useCallback((environmentData) => {
    setEnvironmentsVisited((prev) => prev + 1);
    advanceTutorial("environmentChanged");

    try {
      const parsedEnvironment =
        typeof environmentData === "string"
          ? JSON.parse(environmentData)
          : environmentData;
      const parsedEnvironmentId = String(
        parsedEnvironment?.id ||
          parsedEnvironment?.environmentId ||
          parsedEnvironment?.environmentID ||
          "",
      ).trim();
      setFocusedElement(null);
      setFocusedPanelElementLocked(null);
      setFocusedPanelPositionLocked(null);
      clearElementInformationModeState();
      setCurrentEnvironment(parsedEnvironment);
      setGuideState((previousGuideState) =>
        previousGuideState
          ? {
              ...previousGuideState,
              script: "",
              data: "",
              audioLength: 0,
            }
          : previousGuideState,
      );
      setGuideStateText("");
      setGuideAudioLevel(0);

      if (
        parsedEnvironmentId &&
        pendingGuideStartEnvironmentChangeRef.current?.environmentId ===
          parsedEnvironmentId
      ) {
        pendingGuideStartEnvironmentChangeRef.current.resolve();
      }

      if (parsedEnvironment?.id) {
        const nextEnvironmentName =
          parsedEnvironment.environmentName ||
          parsedEnvironment.displayName ||
          parsedEnvironment.name ||
          parsedEnvironment.id;

        setEnvironmentLookup((prev) => ({
          ...prev,
          [parsedEnvironment.id]: {
            name: nextEnvironmentName,
          },
        }));

        setVisitedEnvironmentNames((prev) =>
          prev.includes(nextEnvironmentName) ? prev : [...prev, nextEnvironmentName],
        );
      }

    } catch (error) {
      console.error("Failed to parse environmentData", error);
    }
  }, [advanceTutorial, clearElementInformationModeState]);

  const handleEnvironmentLoadStatesChanged = useCallback((payload) => {
    const normalizedPayload = normalizeEnvironmentLoadStatesPayload(payload);
    if (normalizedPayload) {
      setEnvironmentLoadStates(normalizedPayload);
    }
  }, []);

  const handleConditionsChanged = useCallback((payload) => {
    const conditionValues = parseConditionChangedEvent(payload);
    if (conditionValues) {
      setEnvironmentConditionValues((currentConditionValues) => ({
        ...currentConditionValues,
        ...conditionValues,
      }));
    }
  }, []);

  const handleRecordingStarted = useCallback(async (instanceId) => {
    if (!voiceRecorderRef.current?.startRecording) {
      setIsRecording(false);
      return;
    }

    try {
      clearMicDeniedGuideContinueTimeout();
      await voiceRecorderRef.current.startRecording(instanceId);
      setMicrophonePermissionDenied(false);
      setIsRecording(true);
    } catch (error) {
      setIsRecording(false);

      if (isMicrophonePermissionError(error)) {
        setMicrophonePermissionDenied(true);
        setQuestionState("idle");
        setQuestionDisplayText("");
        questionIdRef.current = null;
        questionPresignedUrlRef.current = null;
        continueGuideAfterMicrophoneDenial();
        return;
      }

      console.error("Failed to start Unity recording:", error);
    }
  }, [clearMicDeniedGuideContinueTimeout, continueGuideAfterMicrophoneDenial]);

  const updateEmbeddedGuideAudioLevel = useCallback((payload) => {
    const rawLevel = getGuideAudioLevelRawFromPayload(payload, null, {
      allowScalar: false,
    });

    if (rawLevel !== null) {
      setGuideAudioLevel(rawLevel);
    }
  }, []);

  const handleGuideStateChanged = useCallback(
    (guideStateData) => {
      const payloadInsightId = getInsightIdFromGuideStatePayload(guideStateData);
      const guideDisplayUpdate = getGuideDisplayUpdateFromPayload(
        guideStateData,
        "",
        { activeInsightId: activeGuideInsightIdRef.current },
      );
      const guideStateName = guideDisplayUpdate.guideState?.guideState;
      const isTerminalInsightState =
        guideStateName === "INSIGHT_COMPLETED" ||
        guideStateName === "INSIGHT_CANCELLED";

      if (payloadInsightId && !isTerminalInsightState) {
        activeGuideInsightIdRef.current = payloadInsightId;
      }

      setGuideState(guideDisplayUpdate.guideState);
      setGuideStateText(guideDisplayUpdate.guideStateText);
      setGuideInstructionText(
        (previousInstructionText) =>
          getGuideDisplayUpdateFromPayload(
            guideStateData,
            previousInstructionText,
            { activeInsightId: activeGuideInsightIdRef.current },
          ).guideInstructionText,
      );

      if (guideDisplayUpdate.shouldClearAudio) {
        setGuideAudioLevel(0);
      } else if (guideDisplayUpdate.guideState) {
        updateEmbeddedGuideAudioLevel(guideDisplayUpdate.guideState);
      }

      if (guideStateName === "INSIGHT_COMPLETED" && payloadInsightId) {
        completedGuideInsightIdRef.current = payloadInsightId;
      }

      const lastGuideStepId = getLastGuideStepId(guideData);
      const isLastGuideStepComplete =
        guideStateName === "INSIGHT_COMPLETED" &&
        guideStepIdsMatch(payloadInsightId, lastGuideStepId);

      if (
        guideEnabled === true &&
        isLastGuideStepComplete &&
        !guideCompletionPopupShownRef.current
      ) {
        guideCompletionPopupShownRef.current = true;
        setGuideCompletionPopupOpen(true);
      }
    },
    [guideData, guideEnabled, updateEmbeddedGuideAudioLevel],
  );

  const handleGuideAudioLevelChanged = useCallback((payload) => {
    const rawLevel = getGuideAudioLevelRawFromPayload(payload);
    const printablePayload =
      typeof payload === "string" ? payload : JSON.stringify(payload);
    console.log(
      `[guide-audio] guideAudioLevelChanged level=${rawLevel} payload=${printablePayload}`,
    );
    setGuideAudioLevel(rawLevel);
  }, []);

  const handleMicrophoneLevelChange = useCallback((level) => {
    setMicrophoneLevel(clampAudioLevel(level));
  }, []);

  useEffect(() => {
    if (!isGuideStateTalking(guideState)) {
      setGuideAudioLevel(0);
    }
  }, [guideState]);

  useEffect(() => {
    if (!isRecording && questionState !== "recording") {
      setMicrophoneLevel(0);
    }
  }, [isRecording, questionState]);

  const handleSessionStarted = useCallback((newSessionId) => {
    if (typeof newSessionId !== "string" || newSessionId.trim() === "") {
      console.warn("sessionStarted received invalid session id", newSessionId);
      return;
    }

    console.log("SESSION STARTED", newSessionId);
    setSessionId(newSessionId);
  }, []);

  useEffect(() => {
    const sessionStartedPattern = /SessionStarted:\s*([0-9a-fA-F-]{36})/;
    const originalConsoleLog = console.log;

    const patchedConsoleLog = (...args) => {
      if (typeof args[0] === "string") {
        const match = args[0].match(sessionStartedPattern);
        if (match?.[1]) {
          setSessionId((currentSessionId) => currentSessionId || match[1]);
          if (console.log === patchedConsoleLog) {
            console.log = originalConsoleLog;
          }
        }
      }

      originalConsoleLog(...args);
    };

    console.log = patchedConsoleLog;

    return () => {
      if (console.log === patchedConsoleLog) {
        console.log = originalConsoleLog;
      }
    };
  }, []);

  const assessmentComplete = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "CompleteAssessment");
  }, [sendMessageToUnity]);

  const handleUploadAudio = useCallback((presignedUrl) => {
    if (skipNextUnityAudioUploadRef.current) {
      skipNextUnityAudioUploadRef.current = false;
      assessmentComplete();
      return;
    }

    if (!voiceRecorderRef.current?.uploadFileToS3) {
      console.warn("Unity requested an audio upload, but the recorder is not ready.");
      return;
    }

    voiceRecorderRef.current.uploadFileToS3(presignedUrl).catch((error) => {
      if (error?.message === "No recorded audio file is available for upload") {
        assessmentComplete();
        return;
      }

      console.error("Failed to upload Unity audio file:", error);
    });
  }, [assessmentComplete]);

  const sanitizedGuideStateText =
    guideStateText === GUIDE_STATE_PLACEHOLDER_TEXT ? "" : guideStateText;
  const showElementInformationPanel = Boolean(
    elementInformationVisible && elementInformationElement,
  );
  const effectiveGuideState = guideEnabled === false ? null : guideState;
  const effectiveGuideStateText =
    guideEnabled === false ? "" : sanitizedGuideStateText;
  const effectiveGuideInstructionText =
    guideEnabled === false ? "" : guideInstructionText;
  const startOverlayDismissed = guideEnabled !== null;
  const showGuideOverlay = Boolean(
    SHOW_GUIDE_MANAGER_PANEL && systemLoaded && guideEnabled !== null,
  );
  const questionEnabled =
    guideEnabled === false || isGuideIdleForQuestions(effectiveGuideState);

  useEffect(() => {
    if (guideEnabled !== true || !effectiveGuideInstructionText.trim()) {
      return;
    }

    advanceTutorial("instructionShown");
  }, [advanceTutorial, effectiveGuideInstructionText, guideEnabled]);

  useEffect(() => {
    if (!showGuideOverlay) {
      setPointerInGuideArea(false);
    }
  }, [showGuideOverlay]);

  const clearQuestionResetTimeout = useCallback(() => {
    if (questionResetTimeoutRef.current) {
      clearTimeout(questionResetTimeoutRef.current);
      questionResetTimeoutRef.current = null;
    }
  }, []);

  const resetQuestionFlow = useCallback(() => {
    clearQuestionResetTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
      questionAudioRef.current = null;
    }
    questionIdRef.current = null;
    questionPresignedUrlRef.current = null;
    setQuestionState("idle");
    setQuestionDisplayText("");
  }, [clearQuestionResetTimeout]);

  const scheduleQuestionReset = useCallback(
    (delayMs) => {
      clearQuestionResetTimeout();
      questionResetTimeoutRef.current = setTimeout(() => {
        resetQuestionFlow();
      }, delayMs);
    },
    [clearQuestionResetTimeout, resetQuestionFlow],
  );

  const handleReadyToLoadExperience = useCallback(() => {
    setUnityReadyToLoadExperience(true);
    if (androidTouchDevice) {
      sendMessageToUnity(
        "EventDispatcher",
        "SetWebInputMode",
        ANDROID_TOUCH_INPUT_MODE,
      );
    }
  }, [androidTouchDevice, sendMessageToUnity]);

  const handleUserDataApplied = useCallback(
    (appliedUserId) => {
      if (String(appliedUserId || "").trim() === String(userData?.id || "")) {
        setUnityUserDataApplied(true);
      }
    },
    [userData?.id],
  );

  const handleSystemLoaded = useCallback((systemDataValue) => {
    try {
      const parsedSystem =
        typeof systemDataValue === "string"
          ? JSON.parse(systemDataValue)
          : systemDataValue;
      setSystemData((prevSystemData) =>
        mergeSystemData(prevSystemData, parsedSystem),
      );
    } catch (error) {
      console.error("Failed to parse system data from Unity", error);
    }
  }, []);

  const handleSystemFullyLoaded = useCallback(() => {
    setSystemLoaded(true);
  }, []);

  const handleElementDeselected = useCallback(() => {
    if (shouldKeepFocusedElementUi || showElementInformationPanel) {
      return;
    }

    setCurrentElement(null);
    currentElementRef.current = null;
    clearElementInformationModeState();
  }, [
    clearElementInformationModeState,
    shouldKeepFocusedElementUi,
    showElementInformationPanel,
  ]);

  useUnityEventBindings({
    addEventListener,
    removeEventListener,
    onElementSelected: handleElementSelected,
    onElementDeselected: handleElementDeselected,
    onEnvironmentChanged: handleEnvironmentChanged,
    onEnvironmentLoadStatesChanged: handleEnvironmentLoadStatesChanged,
    onConditionsChanged: handleConditionsChanged,
    onRecordingStarted: handleRecordingStarted,
    onUploadAudioFile: handleUploadAudio,
    onReadyToLoadExperience: handleReadyToLoadExperience,
    onUserDataApplied: handleUserDataApplied,
    onSystemLoaded: handleSystemLoaded,
    onSystemFullyLoaded: handleSystemFullyLoaded,
    onAssessmentComplete: handleAssessmentComplete,
    onInstructionVisibilityChanged: handleInstructionVisibilityChanged,
    onInsightStarted: handleInsightStarted,
    onGameStateChanged: handleGameStateChanged,
    onGuideStateChanged: handleGuideStateChanged,
    onGuideAudioLevelChanged: handleGuideAudioLevelChanged,
    onSessionStarted: handleSessionStarted,
  });

  useEffect(() => {
    if (!isGuideStateTalking(guideState)) {
      return undefined;
    }

    const requestGuideAudioLevel = () => {
      console.log(
        `[guide-audio] RequestGuideAudioLevel guideState=${guideState?.guideState || ""}`,
      );
      sendMessageToUnity("EventDispatcher", "RequestGuideAudioLevel");
    };

    requestGuideAudioLevel();
    const intervalId = setInterval(
      requestGuideAudioLevel,
      GUIDE_AUDIO_LEVEL_REQUEST_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [guideState, sendMessageToUnity]);

  useEffect(() => {
    setExperienceData(null);
    setGuideData(null);
    setExperienceRequestError(null);
    setSystemLoaded(false);
    setEnvironmentLoadStates(null);
    setEnvironmentConditionValues({});
    setFocusedElement(null);
    setFocusedPanelElementLocked(null);
    setFocusedPanelPositionLocked(null);
    clearElementInformationModeState();
    setLabelPosition(null);
    setGuideState(null);
    setGuideStateText(GUIDE_STATE_PLACEHOLDER_TEXT);
    setGuideInstructionText("");
    setGuideAudioLevel(0);
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
    clearMicDeniedGuideContinueTimeout();
    clearPendingGuideStartEnvironmentChange(
      new Error("Guide start environment change was cancelled."),
    );
    guideStartPendingRef.current = false;
    resetGuideCompletionTracking();
    skipNextUnityAudioUploadRef.current = false;
    setUnityReadyToLoadExperience(false);
    setUserDataDispatchedToUnity(false);
    setUnityUserDataApplied(false);
    setExperienceDispatchedToUnity(false);
    const storedViewerTutorialState = getStoredViewerTutorialState();
    viewerTutorialCompletedRef.current = isViewerTutorialCompleted(
      storedViewerTutorialState,
    );
    setViewerTutorial(storedViewerTutorialState);

    if (experienceId) {
      ExperiencesAPI.get(
        getExperienceRequestPath(experienceId, experienceRequestMode),
        getExperienceRequestConfig(experienceRequestMode),
      )
        .then((response) => {
          setExperienceData(getApiResponseData(response));
        })
        .catch((error) => {
          if (
            experienceRequestMode === "viewer" &&
            [401, 403].includes(error?.response?.status)
          ) {
            onExperienceAccessDenied?.(error);
            return;
          }

          setExperienceRequestError(error);
          console.error("Failed to fetch experience data", error);
        });
    }
  }, [
    clearMicDeniedGuideContinueTimeout,
    clearElementInformationModeState,
    clearPendingGuideStartEnvironmentChange,
    experienceId,
    experienceRequestMode,
    onExperienceAccessDenied,
    resetGuideCompletionTracking,
  ]);

  useEffect(() => {
    setUserDataDispatchedToUnity(false);
    setUnityUserDataApplied(false);
    setExperienceRequestError(null);
  }, [userData?.jwt]);

  useEffect(() => {
    if (
      !unityReadyToLoadExperience ||
      userDataDispatchedToUnity ||
      !userData?.id
    ) {
      return;
    }

    sendMessageToUnity("EventDispatcher", "SetUserData", JSON.stringify(userData));
    setUserDataDispatchedToUnity(true);
  }, [
    sendMessageToUnity,
    unityReadyToLoadExperience,
    userData,
    userDataDispatchedToUnity,
  ]);

  useEffect(() => {
    if (!userDataDispatchedToUnity || unityUserDataApplied) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setExperienceRequestError(
        new Error(
          "The viewer could not confirm your session. Reload the page and try again.",
        ),
      );
    }, USER_DATA_HANDSHAKE_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [unityUserDataApplied, userDataDispatchedToUnity]);

  useEffect(() => {
    if (
      !unityUserDataApplied ||
      experienceDispatchedToUnity ||
      !experienceId ||
      !experienceData
    ) {
      return;
    }

    sendMessageToUnity("EventDispatcher", "LoadExperience", experienceId);
    setExperienceDispatchedToUnity(true);
  }, [
    experienceData,
    experienceDispatchedToUnity,
    experienceId,
    sendMessageToUnity,
    unityUserDataApplied,
  ]);

  useEffect(() => {
    const guideId = experienceData?.guideId;
    setGuideData(null);
    activeGuideInsightIdRef.current = "";
    completedGuideInsightIdRef.current = "";
    guideCompletionPopupShownRef.current = false;
    setGuideCompletionPopupOpen(false);

    if (!guideId) {
      return undefined;
    }

    let cancelled = false;

    ExperiencesAPI.get(
      getGuideRequestPath(guideId),
      getRuntimeExperiencesRequestConfig(experienceRequestMode),
    )
      .then((response) => {
        if (cancelled) {
          return;
        }

        setGuideData(getApiResponseData(response));
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn("Failed to fetch guide data", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [experienceData?.guideId, experienceRequestMode]);

  useEffect(() => {
    if (guideEnabled !== true || guideCompletionPopupShownRef.current) {
      return;
    }

    const lastGuideStepId = getLastGuideStepId(guideData);
    if (!guideStepIdsMatch(completedGuideInsightIdRef.current, lastGuideStepId)) {
      return;
    }

    guideCompletionPopupShownRef.current = true;
    setGuideCompletionPopupOpen(true);
  }, [guideData, guideEnabled]);

  useEffect(() => {
    if (!experienceData?.systemId) {
      return undefined;
    }

    let cancelled = false;

    ContentAPI.get(`systems/${experienceData.systemId}`, {
      params: CONTENT_REQUEST_PARAMS,
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        setSystemData((prevSystemData) =>
          mergeSystemData(prevSystemData, getApiResponseData(response)),
        );
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to fetch system data", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [experienceData?.systemId]);

  useEffect(() => {
    if (isAppleMobileDevice || typeof navigator === "undefined") {
      return;
    }

    if (!("gpu" in navigator) || typeof navigator.gpu?.requestAdapter !== "function") {
      setWebGpuState({
        status: "unsupported",
        detail: "The WebGPU API is not available in this browser.",
      });
      return;
    }

    let cancelled = false;

    const detectWebGpuSupport = async () => {
      try {
        const adapter = await navigator.gpu.requestAdapter();

        if (cancelled) {
          return;
        }

        if (!adapter) {
          setWebGpuState({
            status: "unsupported",
            detail: "No compatible WebGPU adapter was found.",
          });
          return;
        }

        setWebGpuState({
          status: "supported",
          detail: "",
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        const detail =
          getDiagnosticErrorMessage(error) || "WebGPU initialization failed.";
        setWebGpuState({
          status: "unsupported",
          detail,
        });
      }
    };

    detectWebGpuSupport();

    return () => {
      cancelled = true;
    };
  }, [isAppleMobileDevice]);

  useEffect(() => {
    const guidePromptText = [guideStateText, guideInstructionText]
      .filter((text) => typeof text === "string" && text.trim())
      .join(" ");

    if (!guidePromptText) {
      setHighlightTimelineButton(false);
      return;
    }

    const text = guidePromptText.toLowerCase();
    const triggerPhrases = [
      "dashboard",
      "top right",
      "top-right",
      "timeline",
      "student responses",
      "view your responses",
      "click the icon",
      "click on the icon",
    ];

    const shouldHighlight = triggerPhrases.some((phrase) =>
      text.includes(phrase),
    );
    setHighlightTimelineButton(shouldHighlight);
  }, [guideInstructionText, guideStateText]);

  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const response = await fetch("/environmentLayout.json", {
          cache: "no-cache",
        });
        if (!response.ok) return;
        const data = await response.json();
        setLayoutOverridesAll(data);
      } catch (error) {
        console.warn("Failed to load environment layout overrides", error);
      }
    };

    loadOverrides();
  }, []);

  useEffect(() => {
    if (!layoutOverridesAll || !systemData?.id) {
      setLayoutOverrides(null);
      return;
    }

    const overridesForSystem = layoutOverridesAll[systemData.id];
    setLayoutOverrides(
      overridesForSystem && typeof overridesForSystem === "object"
        ? overridesForSystem
        : null,
    );
  }, [layoutOverridesAll, systemData]);

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const desktopHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-desktop"),
    );
    const phoneHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-phone"),
    );
    const desktopHoverCenterInset = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-center-inset-desktop"),
    );
    const phoneHoverCenterInset = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-center-inset-phone"),
    );
    const resolvedDesktopHoverZone =
      Number.isFinite(desktopHoverZone) && desktopHoverZone > 0
        ? desktopHoverZone
        : 150;
    const resolvedPhoneHoverZone =
      Number.isFinite(phoneHoverZone) && phoneHoverZone > 0
        ? phoneHoverZone
        : 120;
    const desktopCornerFadeSize = parseFloat(
      rootStyles.getPropertyValue("--edge-corner-fade-size"),
    );
    const phoneCornerFadeSize = parseFloat(
      rootStyles.getPropertyValue("--edge-corner-fade-size-compact"),
    );
    const hoverRevealDelayMs = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-reveal-delay-ms"),
    );
    const resolvedDesktopCornerFadeSize =
      Number.isFinite(desktopCornerFadeSize) && desktopCornerFadeSize >= 0
        ? desktopCornerFadeSize
        : 190;
    const resolvedPhoneCornerFadeSize =
      Number.isFinite(phoneCornerFadeSize) && phoneCornerFadeSize >= 0
        ? phoneCornerFadeSize
        : 40;
    const resolvedHoverRevealDelayMs =
      Number.isFinite(hoverRevealDelayMs) && hoverRevealDelayMs >= 0
        ? hoverRevealDelayMs
        : 220;
    const resolvedDesktopHoverCenterInset =
      Number.isFinite(desktopHoverCenterInset) && desktopHoverCenterInset >= 0
        ? desktopHoverCenterInset
        : resolvedDesktopCornerFadeSize;
    const resolvedPhoneHoverCenterInset =
      Number.isFinite(phoneHoverCenterInset) && phoneHoverCenterInset >= 0
        ? phoneHoverCenterInset
        : resolvedPhoneCornerFadeSize;

    const clearEdgeHoverTimeout = () => {
      if (edgeHoverTimeoutRef.current) {
        clearTimeout(edgeHoverTimeoutRef.current);
        edgeHoverTimeoutRef.current = null;
      }
    };

    const applyEdgeHover = (nextHover) => {
      if (!edgeHoverStatesMatch(edgeHoverRef.current, nextHover)) {
        edgeHoverRef.current = nextHover;
        setEdgeHover(nextHover);
      }
    };

    const clearEdgeHover = () => {
      clearEdgeHoverTimeout();
      edgeHoverPendingRef.current = EMPTY_EDGE_HOVER;
      applyEdgeHover(EMPTY_EDGE_HOVER);
    };

    const scheduleEdgeHover = (nextHover) => {
      if (edgeHoverStatesMatch(edgeHoverPendingRef.current, nextHover)) {
        return;
      }

      edgeHoverPendingRef.current = nextHover;
      clearEdgeHoverTimeout();

      const currentHover = edgeHoverRef.current;
      const currentHoverIsCleared =
        !currentHover.left &&
        !currentHover.right &&
        !currentHover.top &&
        !currentHover.bottom;
      const isClearedHover =
        !nextHover.left &&
        !nextHover.right &&
        !nextHover.top &&
        !nextHover.bottom;

      if (isClearedHover) {
        applyEdgeHover(nextHover);
        return;
      }

      if (!currentHoverIsCleared && !edgeHoverStatesMatch(currentHover, nextHover)) {
        applyEdgeHover(EMPTY_EDGE_HOVER);
      }

      edgeHoverTimeoutRef.current = setTimeout(() => {
        edgeHoverTimeoutRef.current = null;
        applyEdgeHover(edgeHoverPendingRef.current);
      }, resolvedHoverRevealDelayMs);
    };

    const handleMouseMove = (event) => {
      const unityContainer = document.querySelector(".content");
      if (!unityContainer) return;

      const rect = unityContainer.getBoundingClientRect();
      const edgeHoverWidth = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;
      const edgeHoverHeight = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;
      const edgeCenterInset = isPhone
        ? resolvedPhoneHoverCenterInset
        : resolvedDesktopHoverCenterInset;

      latestMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      hasObservedMousePositionRef.current = true;

      const x = latestMousePositionRef.current.x;
      const y = latestMousePositionRef.current.y;
      const withinViewport =
        x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
      const verticalTriggerInset = Math.min(edgeCenterInset, rect.height / 2);
      const horizontalTriggerInset = Math.min(edgeCenterInset, rect.width / 2);
      const withinVerticalTriggerBand =
        y >= verticalTriggerInset && y <= rect.height - verticalTriggerInset;
      const withinHorizontalTriggerBand =
        x >= horizontalTriggerInset && x <= rect.width - horizontalTriggerInset;

      const nextHover = {
        left: withinViewport && x <= edgeHoverWidth && withinVerticalTriggerBand,
        right:
          withinViewport &&
          x >= rect.width - edgeHoverWidth &&
          withinVerticalTriggerBand,
        top: withinViewport && y <= edgeHoverHeight && withinHorizontalTriggerBand,
        bottom:
          withinViewport &&
          y >= rect.height - edgeHoverHeight &&
          withinHorizontalTriggerBand,
      };

      scheduleEdgeHover(nextHover);
    };

    const handleWindowMouseOut = (event) => {
      if (!event.relatedTarget && !event.toElement) {
        clearEdgeHover();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearEdgeHover();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleWindowMouseOut);
    window.addEventListener("blur", clearEdgeHover);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearEdgeHoverTimeout();
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleWindowMouseOut);
      window.removeEventListener("blur", clearEdgeHover);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPhone]);

  useEffect(() => {
    const handleMouseMove = () => {
      if (currentElement && elementHasTargetEnvironment(currentElement)) {
        setCursorStyle("zoom-in");
      } else if (currentElement) {
        setCursorStyle("pointer");
      } else {
        setCursorStyle("move");
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [currentElement, elementHasTargetEnvironment]);

  useEffect(() => {
    if (!isElementFocusedGameState) {
      skipNextFocusPlacementRef.current = false;
    }
  }, [isElementFocusedGameState]);

  const handleMouseDown = (event) => {
    const ignoreFocusPlacement = showElementInformationPanel;
    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
      ignoreFocusPlacement,
    };
    if (isElementFocusedGameState || ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
      return;
    }
    skipNextFocusPlacementRef.current = false;
    setFocusedPanelElementLocked(null);
    setFocusedPanelPositionLocked(null);
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleMouseUp = (event) => {
    const threshold = 5;
    const dragStartPosition = dragStart.current ?? {
      x: event.clientX,
      y: event.clientY,
      ignoreFocusPlacement: false,
    };
    const dx = event.clientX - dragStartPosition.x;
    const dy = event.clientY - dragStartPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const pointerPosition = getPointerPositionInElement(event);
    const ignoreFocusPlacement =
      showElementInformationPanel ||
      isElementFocusedGameState ||
      dragStartPosition.ignoreFocusPlacement;

    if (distance <= threshold && !ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = false;
      if (currentElement) {
        setFocusedElement(currentElement);
        advanceTutorial("elementSelected");
      } else {
        setFocusedElement(null);
      }
      const nextLabelPosition = {
        x: pointerPosition.x + playerOffset,
        y: pointerPosition.y,
      };
      latestMousePositionRef.current = nextLabelPosition;
      hasObservedMousePositionRef.current = true;
      setLabelPosition(nextLabelPosition);
    } else if (ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
    } else {
      advanceTutorial("drag");
    }

    dragStart.current = null;
  };

  const updateLatestInteractionPosition = (event, clientX, clientY) => {
    const pointerPosition = getClientPositionInElement(
      event.currentTarget,
      clientX,
      clientY,
    );
    const nextLabelPosition = {
      x: pointerPosition.x + playerOffset,
      y: pointerPosition.y,
    };
    latestMousePositionRef.current = nextLabelPosition;
    hasObservedMousePositionRef.current = true;
    return nextLabelPosition;
  };

  const handleTouchStart = (event) => {
    if (event.touches?.length !== 1) {
      touchDragStartRef.current = null;
      return;
    }

    const touch = event.touches[0];
    const ignoreFocusPlacement = showElementInformationPanel;
    touchDragStartRef.current = touch
      ? {
          x: touch.clientX,
          y: touch.clientY,
          ignoreFocusPlacement,
        }
      : null;

    if (isElementFocusedGameState || ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
      return;
    }

    skipNextFocusPlacementRef.current = false;
    setFocusedPanelElementLocked(null);
    setFocusedPanelPositionLocked(null);
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleTouchEnd = (event) => {
    const startPosition = touchDragStartRef.current;
    const touch = event.changedTouches?.[0];
    touchDragStartRef.current = null;

    if (!startPosition || !touch || event.touches?.length > 0) {
      return;
    }

    const dx = touch.clientX - startPosition.x;
    const dy = touch.clientY - startPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const ignoreFocusPlacement =
      showElementInformationPanel ||
      isElementFocusedGameState ||
      startPosition.ignoreFocusPlacement;

    if (distance <= 8 && !ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = false;
      const nextLabelPosition = updateLatestInteractionPosition(
        event,
        touch.clientX,
        touch.clientY,
      );
      const selectedElement = currentElementRef.current || currentElement;
      if (selectedElement) {
        setFocusedElement(selectedElement);
        setLabelPosition(nextLabelPosition);
        advanceTutorial("elementSelected");
      } else {
        setFocusedElement(null);
      }
    } else if (ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
    } else {
      advanceTutorial("drag");
    }
  };

  const handleTouchCancel = () => {
    touchDragStartRef.current = null;
  };

  useEffect(() => {
    if (!currentElement) {
      clearTimeout(focusTimeoutRef.current);
      if (!shouldKeepFocusedElementUi) {
        setFocusedElement(null);
        setLabelPosition(null);
      }
      return;
    }

    if (!isElementSelectedGameState) {
      clearTimeout(focusTimeoutRef.current);
      return;
    }

    clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(() => {
      if (skipNextFocusPlacementRef.current) {
        skipNextFocusPlacementRef.current = false;
        return;
      }

      const nextLabelPosition = getFinitePoint(latestMousePositionRef.current);
      if (nextLabelPosition && hasObservedMousePositionRef.current) {
        setFocusedElement(currentElement);
        setLabelPosition(nextLabelPosition);
      }
    }, 300);

    return () => clearTimeout(focusTimeoutRef.current);
  }, [currentElement, isElementSelectedGameState, shouldKeepFocusedElementUi]);

  const doneRecording = useCallback(async () => {
    setIsRecording(false);

    if (questionIdRef.current && questionPresignedUrlRef.current) {
      const questionId = questionIdRef.current;
      const presignedUrl = questionPresignedUrlRef.current;
      const audioUploadMetadata = getAudioUploadMetadata(
        questionRecordingFormatRef.current,
      );
      questionPresignedUrlRef.current = null;
      setQuestionState("processing");

      try {
        await voiceRecorderRef.current.uploadFileToS3(presignedUrl, {
          suppressAssessmentComplete: true,
        });

        const viewerQuestionPayload = buildViewerQuestionPayload({
          experienceData,
          systemData,
          currentEnvironment,
          currentElement,
          visitedEnvironmentNames,
          guideState: effectiveGuideState,
          guideStateText: effectiveGuideStateText,
          guideInstructionText: effectiveGuideInstructionText,
        });

        const result = await AssistantAPI.processExperienceQuestionAudio({
          questionId,
          experienceId,
          environmentId: currentEnvironment?.id ?? null,
          elementId: currentElement?.id ?? null,
          sessionId,
          userId: userData?.id ?? null,
          lastExchange: lastExchangeRef.current,
          ...audioUploadMetadata,
          ...viewerQuestionPayload,
        }, userData?.jwt);

        const answerText =
          result.answerText ||
          "I can help with questions about what you are seeing in this experience.";

        if (result.questionText && result.answerText) {
          lastExchangeRef.current = {
            question: result.questionText,
            answer: result.answerText,
          };
        }

        const textVisibleMs = Math.max(
          5000,
          Math.min(12000, answerText.length * 65),
        );

        setQuestionDisplayText(answerText);
        setQuestionState("speaking");

        if (result.outAudioUrl) {
          if (questionAudioRef.current) {
            questionAudioRef.current.pause();
            questionAudioRef.current.onended = null;
            questionAudioRef.current.onerror = null;
          }

          const audio = new Audio(result.outAudioUrl);
          questionAudioRef.current = audio;
          audio.onended = () => scheduleQuestionReset(800);
          audio.onerror = () => scheduleQuestionReset(textVisibleMs);
          audio.play().catch((error) => {
            console.warn("Question answer audio autoplay failed:", error);
            scheduleQuestionReset(textVisibleMs);
          });
        } else {
          scheduleQuestionReset(textVisibleMs);
        }
      } catch (error) {
        console.error("Question processing failed:", error);
        setQuestionState("error");
        setQuestionDisplayText("Something went wrong. Please try again.");
        questionIdRef.current = null;
        questionRecordingFormatRef.current = null;
      }
      return;
    }

    sendMessageToUnity("EventDispatcher", "RecordingStopped");
  }, [
    currentElement,
    currentEnvironment,
    effectiveGuideState,
    effectiveGuideInstructionText,
    effectiveGuideStateText,
    experienceData,
    experienceId,
    scheduleQuestionReset,
    sendMessageToUnity,
    sessionId,
    systemData,
    userData,
    visitedEnvironmentNames,
  ]);

  const stopRecordingFromGuide = useCallback(() => {
    if (voiceRecorderRef.current?.stopRecording) {
      voiceRecorderRef.current.stopRecording();
    }
  }, []);

  const beginQuestionRecording = useCallback(async () => {
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
    const questionId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    questionIdRef.current = questionId;
    questionPresignedUrlRef.current = null;
    const recordingFormat = getPreferredAudioRecordingFormat();
    questionRecordingFormatRef.current = recordingFormat;
    const audioUploadMetadata = getAudioUploadMetadata(recordingFormat);

    try {
      const result = await AssistantAPI.createQuestionAudioUploadUrl({
        questionId,
        experienceId,
        environmentId: currentEnvironment?.id ?? null,
        elementId: currentElement?.id ?? null,
        ...audioUploadMetadata,
      }, userData?.jwt);

      questionPresignedUrlRef.current = result.inAudioUrl;
      if (!voiceRecorderRef.current?.startRecording) {
        throw new Error("Voice recorder is not ready.");
      }

      await voiceRecorderRef.current.startRecording(questionId, recordingFormat);
      setMicrophonePermissionDenied(false);
      setQuestionState("recording");
      setIsRecording(true);
    } catch (error) {
      questionPresignedUrlRef.current = null;
      setIsRecording(false);
      questionIdRef.current = null;
      questionRecordingFormatRef.current = null;

      if (isMicrophonePermissionError(error)) {
        setMicrophonePermissionDenied(true);
        setQuestionState("idle");
        setQuestionDisplayText("");
        return;
      }

      console.error("Failed to start question recording:", error);
      setQuestionState("error");
      setQuestionDisplayText("Something went wrong. Please try again.");
    }
  }, [currentElement, currentEnvironment, experienceId, userData?.jwt]);

  const handleMicPermissionDenied = useCallback((error, options = {}) => {
    void error;
    const keepPromptOpen = Boolean(options.keepPromptOpen);

    setMicrophonePermissionDenied(true);
    setMicPromptEnabled(keepPromptOpen);
    if (!keepPromptOpen) {
      setMicPromptIntent(null);
      setMicPromptShowDeniedInstructions(false);
    }
    setIsRecording(false);
    questionIdRef.current = null;
    questionPresignedUrlRef.current = null;
    questionRecordingFormatRef.current = null;
    setQuestionState("idle");
    setQuestionDisplayText("");
  }, []);

  const handleMicPromptDismiss = useCallback(() => {
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
  }, []);

  const handleMicPermissionGranted = useCallback(() => {
    const shouldStartQuestionRecording = micPromptIntent === "question";

    setMicrophonePermissionDenied(false);
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);

    if (shouldStartQuestionRecording) {
      beginQuestionRecording();
    }
  }, [beginQuestionRecording, micPromptIntent]);

  const handleEnableMicrophone = useCallback(() => {
    setMicPromptIntent("permission");
    setMicPromptShowDeniedInstructions(true);
    setMicPromptEnabled(true);
  }, []);

  const handleAskQuestion = useCallback(() => {
    clearQuestionResetTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
      questionAudioRef.current = null;
    }

    setQuestionState("idle");
    setQuestionDisplayText("");
    setMicPromptIntent("question");
    setMicPromptShowDeniedInstructions(microphonePermissionDenied);
    setMicPromptEnabled(true);
  }, [clearQuestionResetTimeout, microphonePermissionDenied]);

  useEffect(() => () => {
    clearQuestionResetTimeout();
    clearMicDeniedGuideContinueTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
    }
  }, [clearMicDeniedGuideContinueTimeout, clearQuestionResetTimeout]);

  const changeEnvironment = (environmentId) => {
    sendMessageToUnity("EventDispatcher", "ChangeEnvironment", environmentId);
  };

  const startGuide = useCallback(
    async ({ resetToStartEnvironment = false } = {}) => {
      if (guideStartPendingRef.current) {
        return false;
      }

      if (!experienceData?.guideId) {
        throw new Error("The AI guide is unavailable for this experience.");
      }

      guideStartPendingRef.current = true;

      try {
        const startEnvironmentId = getSystemCurrentEnvironmentId(systemData);
        const activeEnvironmentId = String(currentEnvironment?.id || "").trim();

        if (
          resetToStartEnvironment &&
          startEnvironmentId &&
          startEnvironmentId !== activeEnvironmentId
        ) {
          const environmentChangedPromise =
            waitForGuideStartEnvironmentChanged(startEnvironmentId);

          try {
            sendMessageToUnity(
              "EventDispatcher",
              "ChangeEnvironment",
              startEnvironmentId,
            );
          } catch (error) {
            clearPendingGuideStartEnvironmentChange(error);
            await environmentChangedPromise.catch(() => undefined);
            throw error;
          }

          await environmentChangedPromise;
        }

        sendMessageToUnity("EventDispatcher", "LoadGuide", experienceData.guideId);
        return true;
      } finally {
        guideStartPendingRef.current = false;
      }
    },
    [
      clearPendingGuideStartEnvironmentChange,
      currentEnvironment?.id,
      experienceData?.guideId,
      sendMessageToUnity,
      systemData,
      waitForGuideStartEnvironmentChanged,
    ],
  );

  const handleStartGuideSelection = useCallback(() => {
    const previousGuideEnabled = guideEnabled;
    resetGuideCompletionTracking();
    setGuideEnabled(true);
    advanceTutorial("guidePromptSettled");
    startGuide({ resetToStartEnvironment: true }).catch((error) => {
      console.error("Failed to start guide", error);
      setGuideEnabled(previousGuideEnabled);
    });
  }, [
    advanceTutorial,
    guideEnabled,
    resetGuideCompletionTracking,
    startGuide,
  ]);

  const handleSkipGuideSelection = useCallback(() => {
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
    setGuideEnabled(false);
    advanceTutorial("guidePromptDeclined");
  }, [advanceTutorial]);

  const handleRestartGuide = useCallback(
    (event) => {
      event?.stopPropagation?.();
      resetGuideCompletionTracking();
      setGuideEnabled(true);
      startGuide({ resetToStartEnvironment: false }).catch((error) => {
        console.error("Failed to restart guide", error);
      });
    },
    [resetGuideCompletionTracking, startGuide],
  );

  const repeatInsight = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "RepeatInsight");
  }, [sendMessageToUnity]);

  const zoomIn = useCallback((targetEnvironmentId) => {
    if (getEnvironmentNavigationLocked(targetEnvironmentId)) {
      return;
    }

    sendMessageToUnity("EventDispatcher", "ZoomIn");
  }, [getEnvironmentNavigationLocked, sendMessageToUnity]);

  const zoomOut = useCallback((targetEnvironmentId) => {
    if (getEnvironmentNavigationLocked(targetEnvironmentId)) {
      return;
    }

    sendMessageToUnity("EventDispatcher", "ZoomOut");
  }, [getEnvironmentNavigationLocked, sendMessageToUnity]);

  const getDisplayName = (elementData) => {
    const name = getElementDisplayNameValue(elementData);
    return !elementHasTargetEnvironment(elementData)
      ? `${name}`
      : `${name} 🔍`;
  };

  const getSubtitle = (elementData) => {
    return elementHasTargetEnvironment(elementData) && environmentsVisited < 2
      ? "Click for object actions."
      : null;
  };
  const lastUnitySelectedElementName =
    getElementDisplayNameValue(lastUnitySelectedElement);
  const lastUnitySelectedElementId = lastUnitySelectedElement?.id;

  const showEnvironmentMap = Boolean(
    systemLoaded &&
    systemData &&
    isCurrentGameState([
      GAME_STATES.ViewMode,
      GAME_STATES.ViewModeElementSelected,
      GAME_STATES.ViewModeElementFocused,
      GAME_STATES.ZoomingIn,
      GAME_STATES.ZoomingOut,
    ]),
  );
  const showElementLabelPanel = Boolean(
    systemLoaded &&
      !showElementInformationPanel &&
      !pointerInGuideArea &&
      focusedElement &&
      labelPosition &&
      isCurrentGameState([GAME_STATES.ViewModeElementSelected]),
  );
  const focusedPanelElementCandidate =
    lastUnitySelectedElement || currentElement || focusedElement;
  const focusedPanelElementCandidateId =
    focusedPanelElementCandidate?.id || "";
  const focusedPanelElement = shouldKeepFocusedElementUi
    ? isElementFocusedGameState
      ? focusedPanelElementCandidate || focusedPanelElementLocked
      : focusedPanelElementLocked
    : null;
  const focusedPanelElementId = focusedPanelElement?.id || "";
  const showFocusedElementControls = Boolean(
    focusedPanelElement,
  );
  const focusedControlPositionSource =
    getFinitePoint(focusedPanelPositionLocked) || getFinitePoint(labelPosition);
  const focusedZoomButtonPosition = focusedControlPositionSource
    ? getClampedFocusControlPosition(focusedControlPositionSource, isPhone)
    : null;
  const focusedLabelPosition = focusedZoomButtonPosition
    ? {
        x: focusedZoomButtonPosition.x,
        y: focusedZoomButtonPosition.y - zoomButtonSize / 2 - (isPhone ? 20 : 24),
      }
    : null;
  const focusedControlsReady = Boolean(
    focusedZoomReadyElementId === focusedPanelElementId &&
      focusedZoomButtonPosition,
  );
  const showFocusedZoomButton = Boolean(
    focusedControlsReady &&
      !showElementInformationPanel &&
      showFocusedElementControls &&
      elementHasTargetEnvironment(focusedPanelElement),
  );
  const focusedInfoButtonLayout =
    focusedZoomButtonPosition && showFocusedZoomButton
      ? getFocusedInfoButtonLayout(
          focusedZoomButtonPosition,
          zoomButtonSize,
          isPhone,
        )
      : focusedZoomButtonPosition;
  const showFocusedElementInfoButton = Boolean(
    focusedControlsReady &&
      !showElementInformationPanel &&
      showFocusedElementControls &&
      focusedPanelElement &&
      focusedInfoButtonLayout,
  );
  const zoomInTargetEnvironmentId = getElementTargetEnvironmentId(focusedPanelElement);
  const zoomInNavigationLocked = getEnvironmentNavigationLocked(
    zoomInTargetEnvironmentId,
  );
  const zoomOutTargetEnvironmentId = getParentEnvironmentId({
    systemData,
    currentEnvironmentId: currentEnvironment?.id,
    currentEnvironment,
  });
  const zoomOutNavigationLocked = getEnvironmentNavigationLocked(
    zoomOutTargetEnvironmentId,
  );
  const showFocusedElementLabel = Boolean(
    systemLoaded &&
    focusedControlsReady &&
      !showElementInformationPanel &&
      showFocusedElementControls &&
      focusedLabelPosition,
  );
  const showZoomOutButton =
    systemLoaded &&
    webGpuState.status === "supported" &&
    !showElementInformationPanel;
  const blockUnityInput = webGpuState.status === "supported" && !systemLoaded;
  const viewerGestureTutorialStep = getViewerGestureTutorialStep(
    viewerTutorial,
    guideEnabled === true,
  );
  const showViewerGestureTutorial = Boolean(
    viewerGestureTutorialStep &&
      systemLoaded &&
      startOverlayDismissed &&
      !showElementInformationPanel &&
      webGpuState.status === "supported",
  );
  const showGuideInstructionZoomTutorial = Boolean(
    guideEnabled === true &&
      viewerTutorial.zoom === "zoom" &&
      effectiveGuideInstructionText.trim(),
  );
  const isDefaultCompletionCta = completionCtaVariant === "default";
  const guideCompletionMessage = isDefaultCompletionCta
    ? DEFAULT_VIEWER_GUIDE_COMPLETION_MESSAGE
    : GENERIC_GUIDE_COMPLETION_MESSAGE;
  const guideCompletionSecondaryActionLabel = isDefaultCompletionCta
    ? "Get more content"
    : experienceData?.guideId
    ? "Restart guide"
    : "";
  const guideCompletionSecondaryActionHref = isDefaultCompletionCta
    ? PLATFORM_SIGNUP_URL
    : "";
  const guideCompletionSecondaryAction = isDefaultCompletionCta
    ? dismissGuideCompletionPopupForLink
    : handleRestartGuide;

  useEffect(() => {
    setFocusedZoomReadyElementId("");

    if (!focusedPanelElementId) {
      return undefined;
    }

    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      setFocusedZoomReadyElementId(focusedPanelElementId);
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      setFocusedZoomReadyElementId(focusedPanelElementId);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [focusedPanelElementId]);

  const focusedPanelElementLockedId = focusedPanelElementLocked?.id || "";

  useEffect(() => {
    if (!isElementFocusedGameState) {
      return;
    }

    if (!focusedPanelElementCandidate) {
      return;
    }

    setShowTimelinePanel(false);

    if (focusedPanelElementLockedId === focusedPanelElementCandidateId) {
      return;
    }

    setFocusedPanelElementLocked(focusedPanelElementCandidate);
    setFocusedPanelPositionLocked(
      (hasObservedMousePositionRef.current
        ? getFinitePoint(latestMousePositionRef.current)
        : null) || getFinitePoint(labelPosition),
    );
  }, [
    focusedPanelElementCandidate,
    focusedPanelElementCandidateId,
    focusedPanelElementLockedId,
    labelPosition,
    isElementFocusedGameState,
  ]);

  const clearElementLabelOverlay = useCallback(() => {
    clearTimeout(focusTimeoutRef.current);
    setFocusedElement(null);
    setLabelPosition(null);
  }, []);

  const openElementInformation = useCallback(
    (event, elementData) => {
      event?.stopPropagation?.();
      if (!elementData) {
        return;
      }

      clearElementLabelOverlay();
      setShowTimelinePanel(false);
      clearElementInformationModeConfirmationTimeout();
      elementInformationModePendingRef.current = true;
      setElementInformationVisibleState(true);
      setElementInformationElement(elementData);
      console.log(
        "[element-info] requesting EnterInformationMode",
        elementData?.id || "",
      );

      try {
        sendMessageToUnity("EventDispatcher", "EnterInformationMode", "");
        elementInformationModeConfirmationTimeoutRef.current = setTimeout(() => {
          elementInformationModeConfirmationTimeoutRef.current = null;
          elementInformationModePendingRef.current = false;
          if (elementInformationVisibleRef.current) {
            console.warn(
              "[element-info] Unity did not confirm ViewModeElementInformation; closing local information overlay.",
            );
            setElementInformationVisibleState(false);
            setElementInformationElement(null);
          }
        }, 1200);
      } catch (error) {
        console.error("Failed to enter element information mode", error);
        clearElementInformationModeState();
      }
    },
    [
      clearElementInformationModeConfirmationTimeout,
      clearElementInformationModeState,
      clearElementLabelOverlay,
      sendMessageToUnity,
      setElementInformationVisibleState,
    ],
  );

  const closeElementInformation = useCallback(
    (event) => {
      blockOverlayActionEvent(event);
      clearElementInformationModeConfirmationTimeout();
      elementInformationModePendingRef.current = false;
      try {
        sendMessageToUnity("EventDispatcher", "ExitInformationMode", "");
      } catch (error) {
        console.error("Failed to exit element information mode", error);
      }
      setElementInformationVisibleState(false);
      setElementInformationElement(null);
    },
    [
      clearElementInformationModeConfirmationTimeout,
      sendMessageToUnity,
      setElementInformationVisibleState,
    ],
  );

  const clearGuideOverlayElementHover = useCallback(() => {
    clearElementLabelOverlay();
    setCurrentElement(null);
    currentElementRef.current = null;
  }, [clearElementLabelOverlay]);

  const blockGuideOverlayEvent = useCallback((event) => {
    event.stopPropagation();
    if (
      (event.type === "wheel" || event.type === "touchmove") &&
      event.cancelable
    ) {
      event.preventDefault();
    }
  }, []);

  const handleGuideOverlayInteraction = useCallback(
    (event) => {
      blockGuideOverlayEvent(event);
      setPointerInGuideArea(true);
      clearGuideOverlayElementHover();
    },
    [blockGuideOverlayEvent, clearGuideOverlayElementHover],
  );

  const handleGuideOverlayLeave = useCallback(
    (event) => {
      blockGuideOverlayEvent(event);
      setPointerInGuideArea(false);
    },
    [blockGuideOverlayEvent],
  );

  const blockPlatformSignupOverlayEvent = useCallback(
    (event) => {
      event.stopPropagation();
      if (
        (event.type === "wheel" || event.type === "touchmove") &&
        event.cancelable
      ) {
        event.preventDefault();
      }
      clearElementLabelOverlay();
    },
    [clearElementLabelOverlay],
  );

  const openPlatformSignupPanel = useCallback(
    (event) => {
      blockPlatformSignupOverlayEvent(event);
      setPlatformSignupPanelOpen(true);
    },
    [blockPlatformSignupOverlayEvent],
  );

  const closePlatformSignupPanel = useCallback(
    (event) => {
      blockPlatformSignupOverlayEvent(event);
      setPlatformSignupPanelOpen(false);
    },
    [blockPlatformSignupOverlayEvent],
  );

  if (isAppleMobileDevice) {
    return <MobileFallback isPhone={isPhone} />;
  }

  const viewerStartupFailure = getViewerStartupFailure(
    webGpuState,
    initialisationError,
  );
  const viewerStartupFailureDetail =
    viewerStartupFailure === WEBGPU_UNAVAILABLE && androidPlatform
      ? getAndroidWebGpuFallbackDetail()
      : viewerStartupFailure?.detail;

  if (viewerStartupFailure) {
    return (
      <div className="content">
        {!embed && <ViewerHeader system={systemData} />}
        <WebGpuFallback
          isPhone={isPhone}
          title={viewerStartupFailure.title}
          summary={viewerStartupFailure.summary}
          detail={viewerStartupFailureDetail}
        />
      </div>
    );
  }

  if (experienceRequestError) {
    return (
      <div className="content">
        {!embed && <ViewerHeader system={systemData} />}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 520,
              textAlign: "center",
              p: 4,
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "linear-gradient(180deg, rgba(15, 23, 34, 0.96) 0%, rgba(10, 16, 24, 0.98) 100%)",
              boxShadow: "0 32px 80px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.64)" }}>
              VIEWER UNAVAILABLE
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, color: "#fff" }}>
              This experience could not be loaded
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.72)" }}>
              {getExperienceLoadMessage(
                experienceRequestMode,
                experienceRequestError,
              )}
            </Typography>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <GameStateProvider>
      <div
        className="content"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchCancel={handleTouchCancel}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        {webGpuState.status === "checking" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top left, rgba(39, 94, 107, 0.25) 0%, rgba(9, 15, 23, 0) 36%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
              zIndex: 0,
            }}
          />
        )}

        <StatusOverlays
          unityBuildVersion={unityBuildVersionOverlay}
          envLabelBottom={envLabelBottom}
          playerOffset={playerOffset}
          overlayWidth={overlayWidth}
          guideInstructionText={
            showElementInformationPanel ? "" : effectiveGuideInstructionText
          }
          isTouchTutorialDevice={isTouchTutorialDevice}
          zoomTutorialVisible={showGuideInstructionZoomTutorial}
          isPhone={isPhone}
          isPhonePortrait={isPhonePortrait}
          currentGameState={currentGameState}
          lastUnitySelectedElementName={lastUnitySelectedElementName}
          lastUnitySelectedElementId={lastUnitySelectedElementId}
        />

        {webGpuState.status === "supported" && (
          <UnityCanvas
            androidTouchMode={androidTouchDevice}
            unityProvider={unityProvider}
            cursorStyle={cursorStyle}
            inputBlocked={blockUnityInput}
            isLoaded={isLoaded}
            loadingProgression={loadingProgression}
            onApplyPinchGesture={sendPinchGestureToUnity}
            playerOffset={playerOffset}
          />
        )}

        <ViewerGestureTutorial
          isPhone={isPhone}
          isTouchDevice={isTouchTutorialDevice}
          step={showViewerGestureTutorial ? viewerGestureTutorialStep : null}
        />

        {startOverlayDismissed && showZoomOutButton && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: zoomOutButtonBottom,
              transform: "translateX(-50%)",
              zIndex: 1004,
              pointerEvents: "auto",
            }}
          >
            <ZoomControlButton
              ariaLabel="Zoom out"
              disabled={zoomOutNavigationLocked}
              onClick={() => zoomOut(zoomOutTargetEnvironmentId)}
              onWheel={forwardWheelEventToUnityCanvas}
              size={zoomButtonSize}
              testId="viewer-zoom-out"
              title="Click to zoom out"
            >
              <ZoomOutIcon />
            </ZoomControlButton>
          </Box>
        )}

        {!showElementInformationPanel && !embed && <ViewerHeader system={systemData} />}

        {/* Session Timeline Toggle Button - Top Right */}
        {(SHOW_SESSION_INSIGHTS_PANEL || showPlatformSignupCta) &&
          systemLoaded &&
          !showElementInformationPanel && (
          <Box
            sx={{
              position: "absolute",
              top: rightPanelTopViewport,
              right: rightPanelRight,
              zIndex: "var(--start-overlay-chrome-z-index, 2010)",
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isPhonePortrait ? "column-reverse" : "row",
                alignItems: isPhonePortrait ? "flex-end" : "center",
                gap: isPhonePortrait ? 0.85 : 1,
                pointerEvents: "auto",
              }}
            >
              {showPlatformSignupCta && (
                <Box
                  onClick={blockPlatformSignupOverlayEvent}
                  onMouseDown={blockPlatformSignupOverlayEvent}
                  onMouseMove={blockPlatformSignupOverlayEvent}
                  onMouseUp={blockPlatformSignupOverlayEvent}
                  onPointerDown={blockPlatformSignupOverlayEvent}
                  onPointerMove={blockPlatformSignupOverlayEvent}
                  onPointerUp={blockPlatformSignupOverlayEvent}
                  onTouchEnd={blockPlatformSignupOverlayEvent}
                  onTouchMove={blockPlatformSignupOverlayEvent}
                  onTouchStart={blockPlatformSignupOverlayEvent}
                  onWheel={blockPlatformSignupOverlayEvent}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    className="platform-signup__panel"
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      right: 0,
                      width: isPhone ? "min(76vw, 300px)" : 340,
                      ...getViewerPanelPaddingSx(isPhone),
                      borderRadius: "14px",
                      background:
                        "linear-gradient(180deg, rgba(12, 20, 28, 0.96) 0%, rgba(8, 14, 22, 0.92) 100%)",
                      border: "1px solid rgba(133, 245, 236, 0.24)",
                      boxShadow: "0 18px 40px rgba(0, 0, 0, 0.32)",
                      color: "#f2fffc",
                      backdropFilter:
                        "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
                      WebkitBackdropFilter:
                        "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
                      opacity: platformSignupPanelOpen ? 1 : 0,
                      transform: platformSignupPanelOpen
                        ? "translateY(0)"
                        : "translateY(-6px)",
                      transformOrigin: "top right",
                      transition: "opacity 180ms ease, transform 180ms ease",
                      pointerEvents: platformSignupPanelOpen ? "auto" : "none",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        display: "block",
                        mb: 0.4,
                        color: "rgba(161, 241, 224, 0.94)",
                        lineHeight: 1.2,
                        letterSpacing: "0.12em",
                      }}
                    >
                      Full 10k Science
                    </Typography>
                    <Typography
                      sx={{
                        mb: 1.1,
                        fontSize: isPhone ? "0.78rem" : "0.84rem",
                        lineHeight: 1.4,
                        color: "rgba(242, 255, 252, 0.9)",
                      }}
                    >
                      Unlock the full library of interactive science
                      experiences, classroom join codes, and formative
                      assessment tools.
                    </Typography>
                    <Box
                      aria-hidden="true"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 0.65,
                        mb: 1.05,
                      }}
                    >
                      {PLATFORM_PROMO_THUMBNAILS.map((experience) => (
                        <Box
                          key={experience.title}
                          component="img"
                          src={experience.src}
                          alt=""
                          sx={{
                            display: "block",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            objectFit: "cover",
                            borderRadius: "7px",
                            border: "1px solid rgba(210, 255, 249, 0.16)",
                            boxShadow: "0 6px 12px rgba(0, 0, 0, 0.22)",
                          }}
                        />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.75,
                        mb: 0.9,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(161, 241, 224, 0.96)",
                            fontSize: "1rem",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          10+
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(242, 255, 252, 0.74)",
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                          }}
                        >
                          featured experiences
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(161, 241, 224, 0.96)",
                            fontSize: "1rem",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          6-16
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(242, 255, 252, 0.74)",
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                          }}
                        >
                          minute sessions
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        color: "rgba(242, 255, 252, 0.78)",
                        fontSize: "0.74rem",
                        lineHeight: 1.35,
                      }}
                    >
                      Explore CRISPR, invasive species, bacterial genetics,
                      pain science, food chemistry, cellular respiration, air
                      quality, and evolution.
                    </Typography>
                  </Box>

                  <Box
                    component="a"
                    href="https://dashboard.10k.science/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="platform-signup__button"
                    onBlur={closePlatformSignupPanel}
                    onFocus={openPlatformSignupPanel}
                    onPointerEnter={openPlatformSignupPanel}
                    onPointerLeave={closePlatformSignupPanel}
                    onMouseEnter={openPlatformSignupPanel}
                    onMouseLeave={closePlatformSignupPanel}
                    sx={{
                      ...viewerPillActionButtonSx,
                      px: getViewerActionPaddingX(isPhone),
                    }}
                  >
                    <Typography
                      component="span"
                      className="control-label"
                      sx={{
                        ...viewerActionButtonLabelSx,
                        fontSize: isPhone ? "12px" : "13px",
                      }}
                    >
                      {isPhone ? "Full platform" : "Try the full platform"}
                    </Typography>
                    <ExternalLinkIcon
                      sx={{
                        flexShrink: 0,
                        fontSize: isPhone ? 16 : 18,
                      }}
                    />
                  </Box>
                </Box>
              )}

              {SHOW_SESSION_INSIGHTS_PANEL && (
                <SessionTimelineToggleButton
                  isOpen={showTimelinePanel}
                  onClick={() => setShowTimelinePanel((prev) => !prev)}
                  highlight={highlightTimelineButton}
                />
              )}
            </Box>

            {SHOW_SESSION_INSIGHTS_PANEL && showTimelinePanel && (
              <Box
                sx={{
                  width: rightPanelWidth,
                  maxWidth: rightPanelMaxWidth,
                  minWidth: 0,
                  boxSizing: "border-box",
                  pointerEvents: "auto",
                }}
              >
                <SessionTimelinePanel
                  sessionId={sessionId}
                  environmentLookup={environmentLookup}
                  elementLookup={elementLookup}
                  localEvents={localEvents}
                  isOpen={showTimelinePanel}
                  onClose={() => setShowTimelinePanel(false)}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Right-side stack: Guide + Recorder (bottom right) */}
        <Box
          onClick={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onMouseDown={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseEnter={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseLeave={showGuideOverlay ? handleGuideOverlayLeave : undefined}
          onMouseMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseUp={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onPointerDown={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerEnter={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerLeave={showGuideOverlay ? handleGuideOverlayLeave : undefined}
          onPointerMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerUp={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onTouchEnd={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onTouchMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onTouchStart={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onWheel={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          sx={{
            position: "absolute",
            right: guideStackRight,
            bottom: rightPanelBottom,
            width: guideStackWidth,
            maxWidth: guideStackMaxWidth,
            minWidth: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            zIndex: 1000,
            pointerEvents: showGuideOverlay ? "auto" : "none",
            touchAction: showGuideOverlay ? "none" : "auto",
          }}
        >
          {showGuideOverlay && (
            <GuidePrototypes
              guideState={effectiveGuideState}
              guideStateText={effectiveGuideStateText}
              startGuide={handleStartGuideSelection}
              repeatInsight={repeatInsight}
              isRecording={isRecording}
              onStopRecording={stopRecordingFromGuide}
              questionEnabled={questionEnabled}
              questionState={questionState}
              questionDisplayText={questionDisplayText}
              onAskQuestion={handleAskQuestion}
              microphonePermissionDenied={microphonePermissionDenied}
              onEnableMicrophone={handleEnableMicrophone}
              defaultMessage={
                guideEnabled === false
                  ? "Tap the orb to ask about what you are seeing."
                  : ""
              }
              showStartGuideButton={guideEnabled === false}
              guideAudioLevel={
                questionState === "speaking" ? 0 : guideAudioLevel
              }
              microphoneLevel={microphoneLevel}
            />
          )}

          <VoiceRecorder
            doneRecording={doneRecording}
            assessmentComplete={assessmentComplete}
            ref={voiceRecorderRef}
            onVolumeChange={handleMicrophoneLevelChange}
            stacked
            hideUi
          />
        </Box>

        <Box
          component="div"
          sx={{
            position: "absolute",
            transform: showElementLabelPanel
              ? "translateY(0px)"
              : "translateY(8px)",
            opacity: showElementLabelPanel ? 1 : 0,
            transition: "opacity 300ms ease, transform 300ms ease",
            pointerEvents: "none",
            zIndex: 10,
            visibility: showElementLabelPanel ? "visible" : "hidden",
          }}
        >
          {showElementLabelPanel && (
            <Label
              key={focusedElement.id}
              labelPosition={labelPosition}
              displayName={getDisplayName(focusedElement)}
              subtitle={getSubtitle(focusedElement)}
              alignment="auto"
              offsetLeft={0}
            />
          )}
        </Box>

        {showElementInformationPanel && elementInformationElement && (
          <Box
            onClick={stopOverlayPointerEvent}
            onMouseDown={stopOverlayPointerEvent}
            onMouseUp={stopOverlayPointerEvent}
            onPointerDown={stopOverlayPointerEvent}
            onPointerUp={stopOverlayPointerEvent}
            onTouchEnd={stopOverlayPointerEvent}
            onTouchMove={stopOverlayPointerEvent}
            onTouchStart={stopOverlayPointerEvent}
            onWheel={stopOverlayPointerEvent}
            sx={{
              position: "absolute",
              inset: 0,
              color: "#fff",
              pointerEvents: "none",
              touchAction: "none",
              overscrollBehavior: "contain",
              zIndex: 18,
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                ...(isLandscapeViewport && !isPhone
                  ? {
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: "58%",
                      background:
                        "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.56) 38%, rgba(0, 0, 0, 0.94) 100%)",
                    }
                  : {
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: isPhone ? "72%" : "64%",
                      background:
                        "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.64) 42%, rgba(0, 0, 0, 0.94) 100%)",
                    }),
                pointerEvents: "none",
              }}
            />
            <Box
              component="button"
              type="button"
              aria-label="Back to viewer"
              onClick={closeElementInformation}
              onMouseDown={stopOverlayPointerEvent}
              onMouseUp={stopOverlayPointerEvent}
              onPointerCancel={stopOverlayPointerEvent}
              onPointerDown={stopOverlayPointerEvent}
              onPointerUp={stopOverlayPointerEvent}
              onTouchStart={stopOverlayPointerEvent}
              onTouchEnd={stopOverlayPointerEvent}
              sx={{
                ...viewerGhostActionButtonSx,
                position: "absolute",
                top: `calc(env(safe-area-inset-top, 0px) + ${
                  isPhone ? 12 : 16
                }px)`,
                left: `calc(env(safe-area-inset-left, 0px) + ${
                  isPhone ? 12 : 16
                }px)`,
                font: "inherit",
                pointerEvents: "auto",
                "& .MuiSvgIcon-root": {
                  fontSize: 21,
                },
              }}
            >
              <BackArrowIcon
                aria-hidden="true"
                sx={{
                  flexShrink: 0,
                  fontSize: 21,
                }}
              />
              <Typography
                component="span"
                className="control-label"
                sx={{
                  ...viewerActionButtonLabelSx,
                  fontSize: "15px",
                  fontWeight: 700,
                }}
              >
                Back
              </Typography>
            </Box>
            <Box
              sx={{
                position: "absolute",
                ...(isPhone || !isLandscapeViewport
                  ? {
                      left: isPhone
                        ? "calc(env(safe-area-inset-left, 0px) + clamp(24px, 7vw, 72px))"
                        : "50%",
                      bottom: `calc(env(safe-area-inset-bottom, 0px) + ${
                        isPhone
                          ? "clamp(430px, 30vh, 560px)"
                          : "clamp(176px, 18vh, 300px)"
                      })`,
                      transform: isPhone ? "none" : "translateX(-50%)",
                      width: isPhone ? "min(43vw, 420px)" : "min(82vw, 540px)",
	                    }
	                  : {
	                      top: `calc(env(safe-area-inset-top, 0px) + 104px)`,
	                      bottom: `calc(env(safe-area-inset-bottom, 0px) + 132px)`,
	                      left: "66.666vw",
	                      transform: "translateX(-50%)",
	                      width: "min(32vw, 540px)",
	                      display: "flex",
	                      flexDirection: "column",
                      justifyContent: "center",
                    }),
                boxSizing: "border-box",
                maxHeight: isPhone || !isLandscapeViewport ? "42vh" : undefined,
                overflowY: "auto",
                textAlign: "left",
                pointerEvents: "auto",
                touchAction: "pan-y",
              }}
            >
              <Typography
                sx={{
                  color: "var(--label-text, #f2fffc)",
                  fontSize: isPhone ? "1.35rem" : "1.48rem",
                  fontWeight: 700,
                  lineHeight: 1.18,
                  overflowWrap: "anywhere",
                  textAlign: isPhone ? "left" : "center",
                }}
              >
                {getElementDisplayNameValue(elementInformationElement)}
              </Typography>
              {getElementDescriptionValue(elementInformationElement) && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1.4,
                    textAlign: "left",
                    fontSize: isPhone ? "0.92rem" : "0.95rem",
                    lineHeight: 1.55,
                    color: "rgba(242, 255, 252, 0.88)",
                    overflowWrap: "anywhere",
                  }}
                >
                  {getElementDescriptionValue(elementInformationElement)}
                </Typography>
              )}
              {!isPhone && isLandscapeViewport && (
                <Box
                  component="button"
                  type="button"
                  aria-label="Done"
                  onClick={closeElementInformation}
                  onMouseDown={stopOverlayPointerEvent}
                  onMouseUp={stopOverlayPointerEvent}
                  onPointerCancel={stopOverlayPointerEvent}
                  onPointerDown={stopOverlayPointerEvent}
                  onPointerUp={stopOverlayPointerEvent}
                  onTouchStart={stopOverlayPointerEvent}
                  onTouchEnd={stopOverlayPointerEvent}
                  sx={{
                    ...viewerPillActionButtonSx,
                    alignSelf: "center",
                    mt: 2.1,
                    px: viewerCompactActionPaddingX,
                    font: "inherit",
                    pointerEvents: "auto",
                    "& .MuiSvgIcon-root": {
                      ...viewerPillActionButtonSx["& .MuiSvgIcon-root, & svg"],
                      flexShrink: 0,
                      fontSize: 18,
                    },
                  }}
                >
                  <DoneCheckIcon />
                  <Typography
                    component="span"
                    className="control-label"
                    sx={{
                      ...viewerActionButtonLabelSx,
                      fontSize: "14px",
                      fontWeight: "var(--viewer-action-font-weight, 700)",
                    }}
                  >
                    Done
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {!showElementInformationPanel && showFocusedElementControls && (
          <>
            {showFocusedElementLabel && (
              <Box
                sx={{
                  position: "absolute",
                  left: focusedLabelPosition.x,
                  top: focusedLabelPosition.y,
                  zIndex: 1005,
                  pointerEvents: "none",
                }}
              >
                <Label
                  labelPosition={{ x: 0, y: 0 }}
                  displayName={getDisplayName(focusedPanelElement)}
                  subtitle={getSubtitle(focusedPanelElement)}
                  alignment="top"
                  offsetLeft={0}
                />
              </Box>
            )}

            {showFocusedElementInfoButton && focusedInfoButtonLayout && (
              <Box
                sx={{
                  position: "absolute",
                  left: focusedInfoButtonLayout.x,
                  top: focusedInfoButtonLayout.y,
                  transform:
                    focusedInfoButtonLayout.transform || "translate(-50%, -50%)",
                  zIndex: 1005,
                  pointerEvents: "auto",
                  fontSize: 0,
                  width: focusedInfoButtonLayout.width,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label="Explore object"
                  data-testid="viewer-element-info"
                  onClick={(event) =>
                    openElementInformation(event, focusedPanelElement)
                  }
                  onMouseDown={stopOverlayPointerEvent}
                  onMouseUp={stopOverlayPointerEvent}
                  onTouchStart={stopOverlayPointerEvent}
                  onTouchEnd={stopOverlayPointerEvent}
                  sx={{
                    ...viewerPillActionButtonSx,
                    width: "100%",
                    px: getViewerActionPaddingX(isPhone),
                    "& .MuiSvgIcon-root": {
                      flexShrink: 0,
                      fontSize: isPhone ? 15 : 16,
                    },
                  }}
                >
                  <Typography
                    component="span"
                    className="control-label"
                    sx={{
                      ...viewerActionButtonLabelSx,
                      fontSize: isPhone ? "12px" : "13px",
                    }}
                  >
                    Explore object
                  </Typography>
                  <ExploreObjectIcon />
                </Box>
              </Box>
            )}

            {showFocusedZoomButton && (
              <Box
                sx={{
                  position: "absolute",
                  left: focusedZoomButtonPosition.x,
                  top: focusedZoomButtonPosition.y,
                  transform: "translate(-50%, -50%)",
                  zIndex: 1004,
                  pointerEvents: "auto",
                  fontSize: 0,
                }}
              >
                <ZoomControlButton
                  ariaLabel="Zoom into selected element"
                  disabled={zoomInNavigationLocked}
                  onClick={() => zoomIn(zoomInTargetEnvironmentId)}
                  onWheel={forwardWheelEventToUnityCanvas}
                  size={zoomButtonSize}
                  testId="viewer-zoom-in"
                  title="Click to zoom in"
                >
                  <ZoomInIcon />
                </ZoomControlButton>
              </Box>
            )}
          </>
        )}

        {!showElementInformationPanel && showEnvironmentMap && (
          <EnvironmentMap
            systemData={systemData}
            currentEnvironmentId={currentEnvironment?.id}
            changeEnvironment={changeEnvironment}
            layoutOverrides={layoutOverrides}
            environmentConditionValues={environmentConditionValues}
          />
        )}

        {systemLoaded && guideEnabled === null && (
          <StartGuideDialog
            onStartGuide={handleStartGuideSelection}
            onSkip={handleSkipGuideSelection}
          />
        )}
        <GuideCompletionPopup
          isPhone={isPhone}
          message={guideCompletionMessage}
          onPrimaryAction={dismissGuideCompletionPopup}
          onSecondaryAction={guideCompletionSecondaryAction}
          open={guideCompletionPopupOpen}
          primaryActionLabel="Keep exploring"
          secondaryActionHref={guideCompletionSecondaryActionHref}
          secondaryActionLabel={guideCompletionSecondaryActionLabel}
          title={isDefaultCompletionCta ? "Nice work" : "Experience complete"}
        />
        <MicAccessPopup
          enabled={micPromptEnabled}
          onPermissionDenied={handleMicPermissionDenied}
          onPermissionGranted={handleMicPermissionGranted}
          onDismiss={handleMicPromptDismiss}
          showDeniedInstructions={micPromptShowDeniedInstructions}
        />
      </div>
    </GameStateProvider>
  );
};


const UnityBuildStatusScreen = ({ embed, title, detail }) => (
  <div className="content">
    {!embed && <ViewerHeader system={null} />}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at top left, rgba(39, 94, 107, 0.25) 0%, rgba(9, 15, 23, 0) 36%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
        px: 3,
      }}
    >
      <Box sx={{ maxWidth: 520, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          {title}
        </Typography>
        {detail && (
          <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.72)" }}>
            {detail}
          </Typography>
        )}
      </Box>
    </Box>
  </div>
);

const UnityWebViewer = (props) => {
  const manifestSelection = getUnityBuildManifestSelection();
  const manifestUrl = manifestSelection.manifestUrl;
  const manifestErrorMessage = manifestSelection.errorMessage;
  const [buildLoadState, setBuildLoadState] = useState(() => {
    if (manifestErrorMessage) {
      return {
        status: "error",
        config: null,
        error: new Error(manifestErrorMessage),
      };
    }

    if (!manifestUrl) {
      return { status: "ready", config: LOCAL_UNITY_BUILD_CONFIG, error: null };
    }

    return { status: "loading", config: null, error: null };
  });

  useEffect(() => {
    if (manifestErrorMessage) {
      setBuildLoadState({
        status: "error",
        config: null,
        error: new Error(manifestErrorMessage),
      });
      return undefined;
    }

    if (!manifestUrl) {
      setBuildLoadState({
        status: "ready",
        config: LOCAL_UNITY_BUILD_CONFIG,
        error: null,
      });
      return undefined;
    }

    let cancelled = false;
    setBuildLoadState({ status: "loading", config: null, error: null });

    fetch(manifestUrl, { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Unity build manifest request failed: ${response.status} ${response.statusText}`,
          );
        }

        return response.json();
      })
      .then((manifest) => normalizeUnityBuildManifest(manifest, manifestUrl))
      .then((config) => {
        if (!cancelled) {
          setBuildLoadState({ status: "ready", config, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setBuildLoadState({ status: "error", config: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [manifestErrorMessage, manifestUrl]);

  const unityBuildVersionOverlay = manifestSelection.showVersionOverlay
    ? getUnityBuildVersionOverlayLabel(
        buildLoadState.config?.version,
        manifestSelection.versionOverlayLabel,
      )
    : "";

  if (buildLoadState.status === "loading") {
    return (
      <UnityBuildStatusScreen
        embed={props.embed}
        title="Loading 3D viewer"
        detail="Preparing the experience..."
      />
    );
  }

  if (buildLoadState.status === "error") {
    return (
      <UnityBuildStatusScreen
        embed={props.embed}
        title={VIEWER_BUILD_FAILURE.title}
        detail={VIEWER_BUILD_FAILURE.detail}
      />
    );
  }

  return (
    <GameStateProvider>
      <UnityWebViewerContent
        key={buildLoadState.config.buildId || buildLoadState.config.loaderUrl}
        {...props}
        unityBuildConfig={buildLoadState.config}
        unityBuildVersionOverlay={unityBuildVersionOverlay}
      />
    </GameStateProvider>
  );
};



export default UnityWebViewer;
